import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuInfo, LuMail, LuPhone } from "react-icons/lu";
import PhoneInput from "../components/PhoneInput";
import "../styles/BookingDay.scss";
import GoogleMapSection from "../components/GoogleMap";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import LongTermRangePicker from "../components/LongTermRangePicker";
import { useLanguage } from "../context/LanguageContext";
import useAuthToken from "../hooks/useAuthToken";
import usePublicAvailability from "../hooks/usePublicAvailability";
import usePublicPricing from "../hooks/usePublicPricing";
import { getProfile } from "../api/auth";
import {
  checkAvailability,
  createBooking,
  createEventBooking,
  createGuestBooking,
  createGuestEventBooking,
} from "../api/bookings";
import { createEventPaymentIntent, createPaymentIntent, getPaymentIntent } from "../api/payments";
import {
  clearPendingBooking,
  loadPendingBooking,
  savePendingBooking,
  updatePendingBooking,
} from "../utils/paymentStorage";
import {
  BOOKING_LOCATIONS,
  BOOKING_TYPE,
  DAY_EQUIPMENT_KEYS,
  EQUIPMENT_KEY_TO_ID,
  buildItemsPayload,
} from "../bookingCatalog";
import { EQUIPMENT_PRICE_META } from "../constants/equipmentPricing";
import { getLocationEquipment, getLocations } from "../api/locations";
import { getRewardStatus, validateRewardVoucher } from "../api/rewards";
import { getPublicPricingSettings } from "../api/publicSettings";
import {
  buildLocationCoords,
  formatLocationAddress,
  getBlockedDateSet,
  getClosedWeekdays,
  isBlockedDate,
  getWorkingWindowForDate,
} from "../utils/locationUtils";
import { DATE_TIME_PICKER_MAX_MINUTES, DATE_TIME_PICKER_STEP } from "../utils/dateTimePickerConstants";
import { normalizeDateValue, parseDateValue, resolveSelectableDate, toDateKey } from "../utils/dateUtils";
import { getPriceLabel } from "../utils/priceLabel";
import { parsePriceValue } from "../utils/pricing";
import { handleEventImageError } from "../utils/eventImages";
import {
  buildPersistedPricingSummary,
  cloneAppliedVoucher,
  getPricingDisplaySummary,
} from "../utils/bookingPricingSummary";
import {
  buildEquipmentQuantityLookup,
  clampItemsToMax,
  getMaxQuantityForKey,
} from "../utils/inventoryUtils";
import { formatShortDate } from "../utils/dateFormatting";

const AVAILABILITY_DEBOUNCE_MS = 450;
const PAYMENT_INTENT_TIMEOUT_MS = 8000;
const PENDING_REDIRECT_TIMEOUT_MS = 15 * 60 * 1000;
const DAY_ITEM_TITLE_FALLBACKS = {
  sup: "Solo Glide",
  big_sup: "Party Board",
  kayak: "Small Aqua Arrow",
  big_kayak: "Big Aqua Arrow",
  kit: "Kit",
};
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const REWARD_VOUCHER_DISCOUNT_EUR = 20;
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const buildLocationMapUrl = (location) => {
  if (!location) return "";
  const [lat, lng] = Array.isArray(location.coords) ? location.coords : [];
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }

  const query = [location.name, location.subtitle].filter(Boolean).join(", ").trim();
  return query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : "";
};
const normalizeEventCheckout = (value) => {
  if (!value || typeof value !== "object") return null;
  const startDate = new Date(value.startIso);
  const endDate = new Date(value.endIso);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  const priceAmount = Number.parseFloat(value.priceAmount);
  const totalPrice = Number.parseFloat(value.totalPrice);
  const quantity = Number.parseInt(value.quantity, 10);
  const equipmentItems = Array.isArray(value.equipmentItems)
    ? value.equipmentItems
        .map((item) => ({
          equipmentTypeId: item.equipmentTypeId,
          quantity: Number.parseInt(item.quantity, 10) || 0,
        }))
        .filter((item) => item.equipmentTypeId && item.quantity > 0)
    : [];
  const equipmentSummary = Array.isArray(value.equipmentSummary)
    ? value.equipmentSummary
        .map((item) => ({
          name: String(item.name || ""),
          quantity: Number.parseInt(item.quantity, 10) || 0,
        }))
        .filter((item) => item.name && item.quantity > 0)
    : [];
  return {
    id: String(value.id ?? "event"),
    title: String(value.title ?? "Event"),
    description: String(value.description ?? ""),
    location: String(value.location ?? ""),
    mapUrl: String(value.mapUrl ?? ""),
    image: String(value.image ?? "/assets/pos_confirmation.jpg"),
    startDate,
    endDate,
    duration: String(value.duration ?? ""),
    contactEmail: "",
    priceLabel: String(value.priceLabel ?? ""),
    priceAmount: Number.isFinite(priceAmount) ? priceAmount : 0,
    totalPrice: Number.isFinite(totalPrice) ? totalPrice : 0,
    currency: String(value.currency ?? "EUR"),
    isSelfAdjusting: Boolean(value.isSelfAdjusting),
    availablePlaces: Number.parseInt(value.availablePlaces, 10) || 0,
    bookedCount: Number.parseInt(value.bookedCount, 10) || 0,
    remainingPlaces: Number.parseInt(value.remainingPlaces, 10) || 0,
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    equipmentItems,
    equipmentSummary,
  };
};
const buildEventCheckoutSnapshot = (checkout) => {
  if (!checkout) return null;
  return {
    ...checkout,
    startIso: checkout.startDate.toISOString(),
    endIso: checkout.endDate.toISOString(),
  };
};
let stripePromiseCache = null;
const getStripePromise = () => {
  if (!stripePublishableKey) {
    return null;
  }

  if (!stripePromiseCache) {
    stripePromiseCache = loadStripe(stripePublishableKey);
  }

  return stripePromiseCache;
};
const stripeAppearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#11998e",
    colorBackground: "#f8fbfa",
    colorText: "#31326f",
    colorTextSecondary: "#5a8073",
    colorTextPlaceholder: "rgba(90, 128, 115, 0.6)",
    colorIcon: "#111111",
    colorIconHover: "#111111",
    colorIconTabSelected: "#111111",
    colorDanger: "#8a1c24",
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, sans-serif",
    borderRadius: "12px",
  },
  rules: {
    ".Input": {
      border: "2px solid #e7f2f0",
      padding: "12px 14px",
      boxShadow: "none",
      backgroundColor: "#ffffff",
      color: "#31326f",
    },
    ".Input:hover": {
      border: "2px solid #e7f2f0",
    },
    ".Input:focus": {
      border: "2px solid #11998e",
    },
    ".Label": {
      fontWeight: "600",
      color: "#5a8073",
    },
    ".Label:hover": {
      color: "#5a8073",
    },
    ".Block": {
      borderRadius: "14px",
      backgroundColor: "#ffffff",
    },
    ".Tab": {
      padding: "10px 12px",
      borderRadius: "10px",
      backgroundColor: "#f4f8f7",
      color: "#31326f",
      fontWeight: "600",
      border: "1px solid rgba(17, 153, 142, 0.08)",
    },
    ".Tab:hover": {
      backgroundColor: "#f4f8f7",
      color: "#31326f",
      boxShadow: "none",
    },
    ".Tab--selected": {
      backgroundColor: "#11998e",
      color: "#ffffff",
      boxShadow: "0 6px 14px rgba(17, 153, 142, 0.25)",
    },
    ".Tab--selected:hover": {
      backgroundColor: "#11998e",
      color: "#ffffff",
      boxShadow: "0 6px 14px rgba(17, 153, 142, 0.25)",
    },
    ".TabLabel": {
      color: "#31326f",
    },
    ".TabLabel--selected": {
      color: "#ffffff",
    },
    ".TabIcon": {
      color: "#111111",
      fill: "#111111",
    },
    ".TabIcon--selected": {
      color: "#111111",
      fill: "#111111",
    },
  },
};
const stripeAppearanceCompact = {
  ...stripeAppearance,
  rules: {
    ...stripeAppearance.rules,
    ".Tab": {
      ...stripeAppearance.rules[".Tab"],
      padding: "12px 16px",
    },
    ".Tab--selected": {
      ...stripeAppearance.rules[".Tab--selected"],
      padding: "8px 12px",
    },
    ".Tab--selected:hover": {
      ...stripeAppearance.rules[".Tab--selected:hover"],
      padding: "8px 12px",
    },
  },
};

const StripePaymentForm = ({ onSubmit, isPayDisabled, isPaying, t, isCompactPayment }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentElementComplete, setPaymentElementComplete] = useState(false);
  const [paymentElementError, setPaymentElementError] = useState("");
  const [isPaymentElementShaking, setIsPaymentElementShaking] = useState(false);
  const canSubmit = stripe && elements && !isPayDisabled;
  const paymentMethodOrder = ["apple_pay", "google_pay", "card", "crevolut_payd"];
  const shakePaymentElement = useCallback(() => {
    setIsPaymentElementShaking(false);
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => setIsPaymentElementShaking(true));
      setTimeout(() => setIsPaymentElementShaking(false), 500);
      return;
    }
    setIsPaymentElementShaking(true);
    setTimeout(() => setIsPaymentElementShaking(false), 500);
  }, []);
  const showInlinePaymentError = useCallback(
    (message) => {
      setPaymentElementError(
        message ??
          t(
            "bookingDay.payment.cardForm.incomplete",
            "Please complete your card details before paying."
          )
      );
      shakePaymentElement();
    },
    [shakePaymentElement, t]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    const { error } = await elements.submit();
    if (error) {
      showInlinePaymentError(error.message);
      return;
    }

    if (!paymentElementComplete) {
      showInlinePaymentError();
      return;
    }

    setPaymentElementError("");
    const result = await onSubmit(stripe, elements, {
      showInlinePaymentError,
    });
    if (result?.inlineError) {
      showInlinePaymentError(result.inlineError);
    }
  };

  return (
    <form className="card-form stripe-form" onSubmit={handleSubmit}>
      <div
        className={`stripe-form__element${paymentElementError ? " stripe-form__element--invalid" : ""}${isPaymentElementShaking ? " stripe-form__element--shake" : ""}`}
      >
        <PaymentElement
          onChange={(event) => {
            setPaymentElementComplete(Boolean(event.complete));
            setPaymentElementError(event.error?.message ?? "");
          }}
          options={{
            layout: "tabs",
            paymentMethodOrder,
            wallets: {
              applePay: "auto",
              googlePay: "auto",
            },
          }}
        />
      </div>
      {paymentElementError && (
        <p className="contact-error stripe-form__error" role="alert">
          {paymentElementError}
        </p>
      )}
      <button type="submit" className="pay-btn" disabled={!canSubmit}>
        {isPaying
          ? t("bookingDay.payment.processing", "Processing…")
          : t("bookingDay.payment.cardForm.payButton", "Pay Now")}
      </button>
      {isPaying && (
        <div className="pay-loader">
          <div className="spinner" />
          <span>{t("bookingDay.payment.processing", "Processing…")}</span>
        </div>
      )}
    </form>
  );
};
const normalizeGuid = (value) => (typeof value === "string" ? value.toLowerCase() : value);
const isStripeInlineValidationError = (error) => {
  const type = String(error?.type ?? "").toLowerCase();
  const code = String(error?.code ?? "").toLowerCase();
  const declineCode = String(error?.decline_code ?? "").toLowerCase();
  const message = String(error?.message ?? "").toLowerCase();

  if (type === "validation_error") return true;
  if (code.startsWith("incomplete_")) return true;
  if (code.startsWith("invalid_")) return true;
  if (declineCode.startsWith("invalid_")) return true;
  return message.includes("incomplete") || message.includes("unvollst");
};
const normalizeRewardVoucher = (voucher) =>
  voucher
    ? {
      code: voucher.code,
      issuedUtc: voucher.issuedUtc,
      redeemedUtc: voucher.redeemedUtc ?? null,
      redeemed: Boolean(voucher.redeemedUtc),
    }
    : null;

const parsePercentRate = (value) => {
  const match = `${value ?? ""}`.trim().match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const percent = Number.parseFloat(match[1]);
  if (!Number.isFinite(percent) || percent <= 0 || percent > 100) return null;
  return percent / 100;
};

const parseValidityWindow = (value) => {
  const matches = `${value ?? ""}`.match(/\d{4}-\d{2}-\d{2}/g);
  if (!matches || matches.length === 0) return null;
  const start = matches[0] ? new Date(`${matches[0]}T00:00:00`) : null;
  const end = matches[1] ? new Date(`${matches[1]}T23:59:59`) : null;
  if ((start && Number.isNaN(start.getTime())) || (end && Number.isNaN(end.getTime()))) {
    return null;
  }
  return { start, end };
};

const isNowWithinWindow = (window) => {
  if (!window) return true;
  const now = new Date();
  if (window.start && now < window.start) return false;
  if (window.end && now > window.end) return false;
  return true;
};

const findManualPromoByCode = (pricingSettings, code) => {
  const normalizedCode = `${code ?? ""}`.trim().toUpperCase();
  if (!normalizedCode) return null;
  const promos = Array.isArray(pricingSettings?.promoCodes) ? pricingSettings.promoCodes : [];
  return (
    promos.find((promo) => {
      const promoCode = `${promo?.code ?? ""}`.trim().toUpperCase();
      return promoCode === normalizedCode;
    }) ?? null
  );
};

const isManualPromoExhausted = (promo) => {
  if (!promo) return false;
  if (promo.redeemedUtc) return true;
  const limit = Number.parseInt(promo?.usageLimit, 10);
  const count = Number.parseInt(promo?.usageCount, 10);
  return Number.isFinite(limit) && limit > 0 && Number.isFinite(count) && count >= limit;
};

const resolvePromoDiscountFromPricing = (pricingSettings, code) => {
  const normalizedCode = `${code ?? ""}`.trim().toUpperCase();
  if (!normalizedCode) return null;

  const discounts = pricingSettings?.discounts;
  if (discounts?.codeName && `${discounts.codeName}`.trim().toUpperCase() === normalizedCode) {
    const percentRate = parsePercentRate(discounts.discount);
    const fixedAmount = parsePriceValue(discounts.discountAmount);
    const validity = parseValidityWindow(discounts.validity);
    if (!isNowWithinWindow(validity)) return null;
    return {
      code: normalizedCode,
      percentRate,
      fixedAmount,
    };
  }

  const promos = Array.isArray(pricingSettings?.promoCodes) ? pricingSettings.promoCodes : [];
  const matched = promos.find((promo) => {
    const promoCode = `${promo?.code ?? ""}`.trim().toUpperCase();
    if (!promoCode || promoCode !== normalizedCode) return false;
    if (promo?.cancelledUtc) return false;
    if (promo?.redeemedUtc) return false;
    const limit = Number.parseInt(promo?.usageLimit, 10);
    const count = Number.parseInt(promo?.usageCount, 10);
    if (Number.isFinite(limit) && limit > 0 && Number.isFinite(count) && count >= limit) {
      return false;
    }
    const validity = parseValidityWindow(promo?.validity);
    return isNowWithinWindow(validity);
  });

  if (!matched) return null;

  return {
    code: normalizedCode,
    percentRate: parsePercentRate(matched.discountPercent),
    fixedAmount: parsePriceValue(matched.discountAmount),
  };
};

const BookingDay = () => {
  const { t, language } = useLanguage();
  const bookingLocale = language === "de" ? "de-AT" : "en-GB";
  const stripeLocale = language === "de" ? "de" : "en";
  const { isAuthenticated } = useAuthToken();
  const { seasonStartDate, seasonEndDate } = usePublicAvailability();
  const { pricingSettings, pricingMaps, pricingRowsByKey, pricingRowsById } = usePublicPricing();
  const { dayPrices, weeklyPrices, tripPrices } = pricingMaps;
  const isCompactPayment = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 600px)").matches;
  }, []);
  const [availabilityAlert, setAvailabilityAlert] = useState(false);
  const availabilityAlertTimeoutRef = useRef(null);
  const triggerAvailabilityAlert = useCallback(() => {
    if (availabilityAlertTimeoutRef.current) {
      clearTimeout(availabilityAlertTimeoutRef.current);
    }
    setAvailabilityAlert(false);
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => setAvailabilityAlert(true));
    } else {
      setAvailabilityAlert(true);
    }
    availabilityAlertTimeoutRef.current = setTimeout(() => {
      setAvailabilityAlert(false);
    }, 700);
  }, []);
  const priceFallbackLabel = t("bookingDay.items.priceFallback", "Free");
  const getEquipmentPriceLabel = useCallback(
    (type) => {
      const meta = EQUIPMENT_PRICE_META[type];
      if (!meta) return priceFallbackLabel;
      return getPriceLabel(
        { ...meta, priceLabel: priceFallbackLabel },
        pricingRowsByKey,
        pricingRowsById
      );
    },
    [pricingRowsByKey, pricingRowsById, priceFallbackLabel]
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const routeEventCheckout = useMemo(
    () => normalizeEventCheckout(routeLocation.state?.eventCheckout),
    [routeLocation.state]
  );
  const [restoredEventCheckout, setRestoredEventCheckout] = useState(null);
  const eventCheckout = routeEventCheckout ?? restoredEventCheckout;
  const isEventCheckout = Boolean(eventCheckout);
  const today = new Date();
  const todayMinutes = today.getHours() * 60 + today.getMinutes();
  const todayRoundedUpMinutes =
    Math.ceil(todayMinutes / DATE_TIME_PICKER_STEP) * DATE_TIME_PICKER_STEP;
  const todayClampedMinutes = Math.min(todayRoundedUpMinutes, DATE_TIME_PICKER_MAX_MINUTES);
  today.setHours(Math.floor(todayClampedMinutes / 60), todayClampedMinutes % 60, 0, 0);
  const [rangeStartDate, setRangeStartDate] = useState(null);
  const [rangeEndDate, setRangeEndDate] = useState(null);

  // === TIME ===
  const toHHMM = useCallback((d) => {
    if (!(d instanceof Date)) return "";
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }, []);
  const formatDate = useCallback(
    (d) => {
      if (!(d instanceof Date)) return "";
      return formatShortDate(d, bookingLocale);
    },
    [bookingLocale]
  );
  const getMinutesOfDay = (d) => (d instanceof Date ? d.getHours() * 60 + d.getMinutes() : 0);
  const isSameDay = (a, b) =>
    a instanceof Date &&
    b instanceof Date &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const clampDateToWindow = useCallback((date, minMinutes, maxMinutes) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return date;
    const safeMin = Number.isFinite(minMinutes) ? minMinutes : 0;
    const safeMax = Number.isFinite(maxMinutes)
      ? Math.max(safeMin, maxMinutes)
      : DATE_TIME_PICKER_MAX_MINUTES;
    const next = new Date(date);
    const currentMinutes = getMinutesOfDay(next);
    const clampedMinutes = Math.max(safeMin, Math.min(currentMinutes, safeMax));
    next.setHours(Math.floor(clampedMinutes / 60), clampedMinutes % 60, 0, 0);
    return next;
  }, []);
  const createEmptyItems = useCallback(
    () =>
      DAY_EQUIPMENT_KEYS.reduce(
        (acc, key) => ({
          ...acc,
          [key]: 0,
        }),
        {}
      ),
    []
  );

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [items, setItems] = useState(() => createEmptyItems());
  const [step, setStep] = useState(() => (isEventCheckout ? 3 : 0));
  const [transitionDirection, setTransitionDirection] = useState("slide-right");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [, setProfile] = useState(null);
  const [loyaltyVoucher, setLoyaltyVoucher] = useState(null);
  const [voucherInput, setVoucherInput] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [voucherMessage, setVoucherMessage] = useState("");
  const [voucherError, setVoucherError] = useState("");
  const [voucherValidating, setVoucherValidating] = useState(false);
  useEffect(() => {
    if (!eventCheckout) return;
    setStep(3);
    setContactEmail(eventCheckout.contactEmail);
  }, [eventCheckout]);
  useEffect(() => {
    if (appliedVoucher?.kind !== "promo") return;
    if (!appliedVoucher.code) return;
    if (appliedVoucher.promoPercentRate !== null || appliedVoucher.promoFixedAmount !== null) {
      return;
    }

    const code = appliedVoucher.code;
    const local = resolvePromoDiscountFromPricing(pricingSettings, code);
    if (local) {
      setAppliedVoucher((prev) =>
        prev && prev.kind === "promo" && prev.code === code
          ? {
            ...prev,
            promoPercentRate: local.percentRate ?? null,
            promoFixedAmount: local.fixedAmount ?? null,
          }
          : prev
      );
      return;
    }

    getPublicPricingSettings()
      .then((latestPricing) => resolvePromoDiscountFromPricing(latestPricing, code))
      .then((resolved) => {
        if (!resolved) return;
        setAppliedVoucher((prev) =>
          prev && prev.kind === "promo" && prev.code === code
            ? {
              ...prev,
              promoPercentRate: resolved.percentRate ?? null,
              promoFixedAmount: resolved.fixedAmount ?? null,
            }
            : prev
        );
      })
      .catch(() => { });
  }, [
    appliedVoucher?.kind,
    appliedVoucher?.code,
    appliedVoucher?.promoFixedAmount,
    appliedVoucher?.promoPercentRate,
    pricingSettings,
  ]);
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [latestBooking, setLatestBooking] = useState(null);
  const [locationEquipmentByTypeId, setLocationEquipmentByTypeId] = useState(null);
  const [locationEquipmentLoading, setLocationEquipmentLoading] = useState(false);
  const [locationEquipmentError, setLocationEquipmentError] = useState("");
  const handleSelectLocation = useCallback(
    (locationId) => {
      setSelectedLocationId(locationId);
      setItems(createEmptyItems());
    },
    [createEmptyItems]
  );

  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState("");
  const locationMetaById = useMemo(
    () => new Map(BOOKING_LOCATIONS.map((loc) => [loc.id, loc])),
    []
  );

  useEffect(() => {
    let cancelled = false;
    setLocationsLoading(true);
    setLocationsError("");

    getLocations()
      .then((payload) => {
        if (cancelled) return;
        const items = Array.isArray(payload) ? payload : [];
        const normalized = items.map((location) => {
          const meta = locationMetaById.get(location.id);
          return {
            id: location.id,
            name: location.name ?? meta?.name ?? "",
            subtitle: formatLocationAddress(location) || meta?.subtitle || "",
            coords:
              buildLocationCoords(location.latitude, location.longitude) ??
              meta?.coords ??
              [],
            operatingHours:
              location.operatingHours?.trim() ?? meta?.operatingHours ?? "",
            blockedDates: Array.isArray(location.blockedDates) ? location.blockedDates : [],
            bookingLeadTimeHours: Number.isFinite(location.bookingLeadTimeHours)
              ? location.bookingLeadTimeHours
              : 0,
            supportsDayBookings:
              location.supportsDayBookings ?? meta?.supportsDayBookings ?? true,
            supportsNightBookings:
              location.supportsNightBookings ?? meta?.supportsNightBookings ?? true,
          };
        });
        setLocations(normalized);
      })
      .catch((error) => {
        if (cancelled) return;
        setLocationsError(
          error?.message ??
          t("bookingDay.locations.loadError", "Unable to load locations right now.")
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLocationsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [t, locationMetaById]);

  // === inside BookingDay component ===

  const [validationMsg, setValidationMsg] = useState("");

  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (validationMsg) {
      setFadeOut(false); // reset if new message appears

      const fadeTimer = setTimeout(() => setFadeOut(true), 2000); // wait 2s, then fade
      const clearTimer = setTimeout(() => {
        setValidationMsg("");
        setFadeOut(false);
      }, 2800); // 2000ms + 800ms (match CSS transition)

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [validationMsg]);

  useEffect(() => {
    return () => {
      if (availabilityAlertTimeoutRef.current) {
        clearTimeout(availabilityAlertTimeoutRef.current);
      }
    };
  }, []);

  const availableDayLocations = useMemo(
    () =>
      locations.filter(
        (loc) => (loc.supportsDayBookings ?? true) || (loc.supportsNightBookings ?? false)
      ),
    [locations]
  );

  useEffect(() => {
    if (!availableDayLocations.length) {
      setSelectedLocationId(null);
      return;
    }
    if (
      selectedLocationId &&
      availableDayLocations.some((loc) => loc.id === selectedLocationId)
    ) {
      return;
    }
    setSelectedLocationId(availableDayLocations[0].id);
  }, [availableDayLocations, selectedLocationId]);

  const selectedLocation = useMemo(() => {
    if (!availableDayLocations.length) return null;
    return (
      availableDayLocations.find((loc) => loc.id === selectedLocationId) ??
      availableDayLocations[0]
    );
  }, [availableDayLocations, selectedLocationId]);
  const selectedLocationMapUrl = useMemo(
    () => buildLocationMapUrl(selectedLocation),
    [selectedLocation]
  );

  const closedWeekdays = useMemo(
    () => getClosedWeekdays(selectedLocation?.operatingHours ?? ""),
    [selectedLocation]
  );
  const blockedDates = useMemo(
    () => selectedLocation?.blockedDates ?? [],
    [selectedLocation]
  );
  const blockedDateSet = useMemo(() => getBlockedDateSet(blockedDates), [blockedDates]);
  const locationLeadTimeHours = Number(selectedLocation?.bookingLeadTimeHours ?? 0) || 0;
  const locationLeadTimeStart = useMemo(() => {
    const next = new Date();
    next.setHours(next.getHours() + Math.max(locationLeadTimeHours, 0), next.getMinutes(), 0, 0);
    return next;
  }, [locationLeadTimeHours]);
  const locationLeadTimeStartMinutes = useMemo(
    () =>
      Math.min(
        DATE_TIME_PICKER_MAX_MINUTES,
        Math.ceil(getMinutesOfDay(locationLeadTimeStart) / DATE_TIME_PICKER_STEP) *
          DATE_TIME_PICKER_STEP
      ),
    [locationLeadTimeStart]
  );
  const selectableCalendarStartDate = useMemo(() => {
    const seasonStart = seasonStartDate ? new Date(seasonStartDate) : null;
    const baseDate =
      seasonStart && seasonStart > locationLeadTimeStart ? seasonStart : locationLeadTimeStart;
    const initialDay = normalizeDateValue(baseDate, false) ?? new Date(baseDate);
    const latestDay = seasonEndDate ? normalizeDateValue(seasonEndDate, false) : null;

    for (let offset = 0; offset < 366; offset += 1) {
      const candidate = new Date(initialDay);
      candidate.setDate(initialDay.getDate() + offset);
      if (latestDay && candidate > latestDay) break;
      if (blockedDateSet.has(toDateKey(candidate))) continue;
      if (closedWeekdays.includes((candidate.getDay() + 6) % 7)) continue;

      const window = getWorkingWindowForDate(selectedLocation?.operatingHours ?? "", candidate);
      if (window.hasHours === false) continue;

      const dayMinMinutes = Math.max(
        window.minMinutes ?? 0,
        isSameDay(candidate, locationLeadTimeStart) ? locationLeadTimeStartMinutes : 0
      );

      if (dayMinMinutes <= (window.maxMinutes ?? DATE_TIME_PICKER_MAX_MINUTES)) {
        const result = new Date(candidate);
        result.setHours(
          Math.floor(dayMinMinutes / 60),
          dayMinMinutes % 60,
          0,
          0
        );
        return result;
      }
    }

    return baseDate;
  }, [
    blockedDateSet,
    closedWeekdays,
    locationLeadTimeStart,
    locationLeadTimeStartMinutes,
    seasonEndDate,
    seasonStartDate,
    selectedLocation,
  ]);
  const startWorkingWindow = useMemo(() => {
    const anchorDate = rangeStartDate ?? selectableCalendarStartDate ?? locationLeadTimeStart;
    const window = getWorkingWindowForDate(selectedLocation?.operatingHours ?? "", anchorDate);
    return {
      minMinutes: window.minMinutes ?? 0,
      maxMinutes: window.maxMinutes ?? DATE_TIME_PICKER_MAX_MINUTES,
      hasHours: window.hasHours !== false,
    };
  }, [locationLeadTimeStart, rangeStartDate, selectableCalendarStartDate, selectedLocation]);

  const endWorkingWindow = useMemo(() => {
    const anchorDate =
      rangeEndDate ?? rangeStartDate ?? selectableCalendarStartDate ?? locationLeadTimeStart;
    const window = getWorkingWindowForDate(selectedLocation?.operatingHours ?? "", anchorDate);
    return {
      minMinutes: window.minMinutes ?? 0,
      maxMinutes: window.maxMinutes ?? DATE_TIME_PICKER_MAX_MINUTES,
      hasHours: window.hasHours !== false,
    };
  }, [locationLeadTimeStart, rangeEndDate, rangeStartDate, selectableCalendarStartDate, selectedLocation]);
  const selectableStartMinMinutes = useMemo(
    () =>
      Math.max(
        startWorkingWindow.minMinutes,
        isSameDay(rangeStartDate, locationLeadTimeStart) ? locationLeadTimeStartMinutes : 0
      ),
    [locationLeadTimeStart, locationLeadTimeStartMinutes, rangeStartDate, startWorkingWindow.minMinutes]
  );
  const selectableStartMaxMinutes = startWorkingWindow.maxMinutes;
  const selectableEndMinMinutes = endWorkingWindow.minMinutes;
  const selectableEndMaxMinutes = endWorkingWindow.maxMinutes;

  useEffect(() => {
    if (!selectedLocation) return;
    if (!(rangeStartDate instanceof Date) || Number.isNaN(rangeStartDate.getTime())) {
      if (rangeEndDate !== null) {
        setRangeEndDate(null);
      }
      return;
    }

    const nextStartDate = resolveSelectableDate({
      value: rangeStartDate,
      minDate: selectableCalendarStartDate,
      maxDate: seasonEndDate,
      disabledWeekdays: closedWeekdays,
      blockedDates: blockedDateSet,
    });

    if (!(nextStartDate instanceof Date) || Number.isNaN(nextStartDate.getTime())) return;

    let changed = false;
    let startNext = new Date(rangeStartDate);
    const startTimeOutOfBounds =
      getMinutesOfDay(startNext) < selectableStartMinMinutes ||
      getMinutesOfDay(startNext) > startWorkingWindow.maxMinutes;
    const startDateNeedsReset =
      !isSameDay(nextStartDate, rangeStartDate) ||
      startWorkingWindow.hasHours === false ||
      isBlockedDate(blockedDates, rangeStartDate) ||
      startNext.getTime() < locationLeadTimeStart.getTime();
    if (startDateNeedsReset) {
      const nextWindow = getWorkingWindowForDate(selectedLocation.operatingHours ?? "", nextStartDate);
      startNext = new Date(nextStartDate);
      const nextStartMinMinutes = Math.max(
        nextWindow.minMinutes ?? 0,
        isSameDay(nextStartDate, locationLeadTimeStart) ? locationLeadTimeStartMinutes : 0
      );
      startNext.setHours(
        Math.floor(nextStartMinMinutes / 60),
        nextStartMinMinutes % 60,
        0,
        0
      );
      changed = true;
    } else if (startTimeOutOfBounds) {
      startNext = clampDateToWindow(
        startNext,
        selectableStartMinMinutes,
        selectableStartMaxMinutes
      );
      changed = true;
    }

    let endNext =
      rangeEndDate instanceof Date && !Number.isNaN(rangeEndDate.getTime())
        ? new Date(rangeEndDate)
        : null;
    const endTimeOutOfBounds =
      endNext &&
      (getMinutesOfDay(endNext) < selectableEndMinMinutes ||
        getMinutesOfDay(endNext) > selectableEndMaxMinutes);
    if (endNext && endWorkingWindow.hasHours && endTimeOutOfBounds) {
      endNext = clampDateToWindow(endNext, selectableEndMinMinutes, selectableEndMaxMinutes);
      changed = true;
    }
    const endNotAfterStartDay =
      endNext &&
      normalizeDateValue(endNext, false) <= normalizeDateValue(startNext, false);
    if (endNotAfterStartDay) {
      const candidateEndDate = new Date(startNext);
      candidateEndDate.setDate(candidateEndDate.getDate() + 1);
      const resolvedEndDate = resolveSelectableDate({
        value: candidateEndDate,
        minDate: candidateEndDate,
        maxDate: seasonEndDate,
        disabledWeekdays: closedWeekdays,
        blockedDates: blockedDateSet,
        fallbackValue: candidateEndDate,
      });

      if (!(resolvedEndDate instanceof Date) || Number.isNaN(resolvedEndDate.getTime())) {
        endNext = null;
      } else {
        endNext = new Date(resolvedEndDate);
        const nextEndWindow = getWorkingWindowForDate(selectedLocation.operatingHours ?? "", endNext);
        if (nextEndWindow.hasHours) {
          endNext.setHours(
            Math.floor((nextEndWindow.minMinutes ?? 0) / 60),
            (nextEndWindow.minMinutes ?? 0) % 60,
            0,
            0
          );
        }
      }
      changed = true;
    }

    if (changed) {
      setRangeStartDate(startNext);
      setRangeEndDate(endNext);
    }
  }, [
    blockedDateSet,
    blockedDates,
    closedWeekdays,
    rangeEndDate,
    rangeStartDate,
    seasonEndDate,
    selectableCalendarStartDate,
    selectedLocation,
    locationLeadTimeStart,
    locationLeadTimeStartMinutes,
    startWorkingWindow.hasHours,
    startWorkingWindow.maxMinutes,
    selectableEndMaxMinutes,
    selectableEndMinMinutes,
    selectableStartMaxMinutes,
    selectableStartMinMinutes,
    clampDateToWindow,
    endWorkingWindow.hasHours,
    selectedLocation?.operatingHours,
  ]);

  const bookingItemsPayload = useMemo(
    () => buildItemsPayload(items, DAY_EQUIPMENT_KEYS),
    [items]
  );

  const availableEquipmentKeys = useMemo(() => {
    if (!locationEquipmentByTypeId) {
      return DAY_EQUIPMENT_KEYS;
    }

    return DAY_EQUIPMENT_KEYS.filter((key) => {
      const max = getMaxQuantityForKey(key, EQUIPMENT_KEY_TO_ID, locationEquipmentByTypeId);
      return typeof max === "number" && max > 0;
    });
  }, [locationEquipmentByTypeId]);

  useEffect(() => {
    setItems((prev) =>
      clampItemsToMax(prev, DAY_EQUIPMENT_KEYS, EQUIPMENT_KEY_TO_ID, locationEquipmentByTypeId)
    );
  }, [locationEquipmentByTypeId]);

  const hasSelectedEquipment = bookingItemsPayload.length > 0;
  const hasSelectedPrimaryEquipment = DAY_EQUIPMENT_KEYS.some(
    (key) => key !== "kit" && (items[key] ?? 0) > 0
  );
  const startUtc = useMemo(
    () => (rangeStartDate instanceof Date ? new Date(rangeStartDate) : null),
    [rangeStartDate]
  );
  const endUtc = useMemo(
    () => (rangeEndDate instanceof Date ? new Date(rangeEndDate) : null),
    [rangeEndDate]
  );
  const durationHours = useMemo(() => {
    if (!startUtc || !endUtc) return 0;
    return Math.max(0, (endUtc.getTime() - startUtc.getTime()) / 3600000);
  }, [endUtc, startUtc]);
  const durationDays = useMemo(() => durationHours / 24, [durationHours]);

  const startIso = startUtc?.toISOString() ?? null;
  const endIso = endUtc?.toISOString() ?? null;
  const timeSlotLabel = useMemo(() => {
    if (!startUtc || !endUtc) return null;
    return `${formatDate(startUtc)} ${toHHMM(startUtc)} → ${formatDate(endUtc)} ${toHHMM(endUtc)}`;
  }, [endUtc, formatDate, startUtc, toHHMM]);
  const formatDateTimeLabel = useCallback(
    (value) =>
      value instanceof Date && !Number.isNaN(value.getTime())
        ? `${toHHMM(value)} · ${formatDate(value)}`
        : t("bookingDay.summary.noneSelected", "Not selected"),
    [formatDate, t, toHHMM]
  );
  const durationDaysLabel = useMemo(() => {
    if (!(durationDays > 0)) {
      return t("bookingDay.summary.noneSelected", "Not selected");
    }

    const roundedUpDays = Math.max(1, Math.ceil(durationDays));

    if (roundedUpDays === 1) {
      return t("bookingDay.summary.durationDaysSingle", "1 day");
    }

    return t("bookingDay.summary.durationDaysPlural", "{count} days").replace(
      "{count}",
      String(roundedUpDays)
    );
  }, [durationDays, t]);
  const isTimeSlotValid = (() => {
    if (!startUtc || !endUtc) return false;
    if (endUtc <= startUtc) return false;
    if (isBlockedDate(blockedDates, startUtc) || isBlockedDate(blockedDates, endUtc)) {
      return false;
    }

    const startMinutes = getMinutesOfDay(startUtc);
    const endMinutes = getMinutesOfDay(endUtc);
    if (!startWorkingWindow.hasHours || !endWorkingWindow.hasHours) return false;
    if (startMinutes < startWorkingWindow.minMinutes || startMinutes > startWorkingWindow.maxMinutes) {
      return false;
    }
    if (endMinutes < endWorkingWindow.minMinutes || endMinutes > endWorkingWindow.maxMinutes) {
      return false;
    }
    if (startUtc.getTime() < locationLeadTimeStart.getTime()) return false;
    return durationHours > 0;
  })();
  const hasBlockedPickupOrReturn = Boolean(
    startUtc &&
    endUtc &&
    (isBlockedDate(blockedDates, startUtc) || isBlockedDate(blockedDates, endUtc))
  );
  const requiresAvailability = Boolean(
    selectedLocationId && startIso && endIso && hasSelectedEquipment && isTimeSlotValid
  );
  const canSubmitBooking =
    requiresAvailability &&
    !availabilityLoading &&
    !availabilityError &&
    (!availabilityResult || availabilityResult.isAvailable);

  const equipmentKeyByTypeId = useMemo(() => {
    const map = new Map();
    DAY_EQUIPMENT_KEYS.forEach((key) => {
      const id = EQUIPMENT_KEY_TO_ID[key];
      if (id) map.set(normalizeGuid(id), key);
    });
    return map;
  }, []);

  const unavailableEquipment = useMemo(() => {
    if (!availabilityResult || availabilityResult.isAvailable) return [];
    const resultItems = Array.isArray(availabilityResult.items) ? availabilityResult.items : [];
    return resultItems
      .filter((item) => (item?.requestedQuantity ?? 0) > (item?.availableQuantity ?? 0))
      .map((item) => {
        const typeId = normalizeGuid(item?.equipmentTypeId);
        const key = typeId ? equipmentKeyByTypeId.get(typeId) : null;
        const title = key
          ? t(
              `bookingDay.items.${key}.title`,
              DAY_ITEM_TITLE_FALLBACKS[key] ?? "Equipment"
            )
          : t("bookingDay.availability.unknownEquipment", "Equipment");
        return {
          id: typeId || `${title}-${item?.requestedQuantity ?? 0}-${item?.availableQuantity ?? 0}`,
          title,
          requested: item?.requestedQuantity ?? 0,
          available: item?.availableQuantity ?? 0,
          unavailableDates: Array.isArray(item?.unavailableDates)
            ? item.unavailableDates.filter((value) => typeof value === "string" && value.trim())
            : [],
        };
      });
  }, [availabilityResult, equipmentKeyByTypeId, t]);
  const unavailableDateLabels = useMemo(() => {
    const uniqueDates = Array.from(
      new Set(unavailableEquipment.flatMap((line) => line.unavailableDates))
    ).sort((left, right) => left.localeCompare(right));

    return uniqueDates.map((value) => {
      const parsed = parseDateValue(value);
      return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? formatDate(parsed) : value;
    });
  }, [formatDate, unavailableEquipment]);
  const unavailableDateSummary = useMemo(() => {
    if (unavailableDateLabels.length === 0) {
      return t(
        "bookingDay.availability.unavailableShort",
        "Some gear is unavailable. Try another slot or adjust quantities."
      );
    }

    const formattedDates = new Intl.ListFormat(bookingLocale, {
      style: "long",
      type: "conjunction",
    }).format(unavailableDateLabels);

    return t(
      "bookingDay.availability.unavailableDates",
      "Selected gear is fully booked on {dates}. Please choose other dates or reduce quantities."
    ).replace("{dates}", formattedDates);
  }, [bookingLocale, t, unavailableDateLabels]);

  // Validation logic for each step
  const canProceed = () => {
    if (step === 0 && !selectedLocationId) {
      setValidationMsg(
        t(
          "bookingDay.validation.selectLocation",
          "Please select a location before continuing."
        )
      );
      return false;
    }
    if (step === 1 && (!hasSelectedEquipment || !hasSelectedPrimaryEquipment)) {
      setValidationMsg(
        t(
          "bookingDay.validation.selectEquipment",
          "Please select at least one piece of equipment."
        )
      );
      return false;
    }
    if (step === 2) {
      if (!startIso) {
        setValidationMsg(
          t(
            "bookingDay.validation.selectDate",
            "Please select a date and time before continuing."
          )
        );
        return false;
      }

      if (!endIso) {
        setValidationMsg(
          t(
            "bookingDay.validation.selectEndDate",
            "Please choose an end date before continuing."
          )
        );
        return false;
      }

      if (startUtc && endUtc && hasBlockedPickupOrReturn) {
        setValidationMsg(
          t(
            "bookingDay.validation.blockedPickupReturn",
            "Pickup or return is not available on one of the selected dates."
          )
        );
        return false;
      }

      if (startUtc && endUtc && !isTimeSlotValid) {
        setValidationMsg(
          t(
            "bookingDay.validation.outsideHours",
            "Please choose a valid rental range within this location’s available hours."
          )
        );
        return false;
      }

      if (requiresAvailability && availabilityResult && !availabilityResult.isAvailable) {
        setValidationMsg("");
        triggerAvailabilityAlert();
        return false;
      }
    }
    setValidationMsg(""); // clear message
    return true;
  };

  // === Payment UI state ===
  const [isPaying, setIsPaying] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);
  const [payFailed, setPayFailed] = useState(false);
  const [payError, setPayError] = useState(null);
  const [payStuck, setPayStuck] = useState(false);
  const [payPendingRedirect, setPayPendingRedirect] = useState(false);
  const [contactError, setContactError] = useState("");
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [paymentIntentError, setPaymentIntentError] = useState("");
  const [persistedPricingSummary, setPersistedPricingSummary] = useState(null);
  const readRedirectParam = useCallback(
    (key) => {
      const value = searchParams.get(key);
      if (value) return value;
      if (typeof window === "undefined") return null;
      const search = window.location.search;
      if (search) {
        const queryParams = new URLSearchParams(search);
        const queryValue = queryParams.get(key);
        if (queryValue) return queryValue;
      }
      const hash = window.location.hash;
      if (!hash) return null;
      const normalizedHash = hash.startsWith("#") ? hash.slice(1) : hash;
      const queryStart = normalizedHash.indexOf("?");
      const hashQuery = queryStart >= 0 ? normalizedHash.slice(queryStart + 1) : normalizedHash;
      if (!hashQuery) return null;
      const hashParams = new URLSearchParams(hashQuery);
      return hashParams.get(key);
    },
    [searchParams]
  );
  const buildReturnUrl = () => {
    if (typeof window === "undefined") return "";
    const { origin, pathname, hash } = window.location;
    if (hash) {
      const cleanHash = hash.split("?")[0];
      return `${origin}${pathname}${cleanHash}`;
    }
    return `${origin}${pathname}`;
  };
  const redirectStatus = readRedirectParam("redirect_status");
  const redirectPaymentIntent = readRedirectParam("payment_intent");
  const redirectClientSecret = readRedirectParam("payment_intent_client_secret");
  const redirectIntentId =
    redirectPaymentIntent ||
    (redirectClientSecret ? redirectClientSecret.split("_secret_")[0] : null);
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  const isValidE164 = (value) => /^\+[1-9]\d{7,14}$/.test(value.trim());

  const handleContactEmailChange = (value) => {
    setContactEmail(value.slice(0, 128));
  };

  const handleContactPhoneChange = (value) => {
    setContactPhone(value);
  };

  useEffect(() => {
    if (
      contactEmail.trim() &&
      isValidEmail(contactEmail) &&
      contactPhone.trim() &&
      isValidE164(contactPhone)
    ) {
      setContactError("");
    }
  }, [contactEmail, contactPhone]);

  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null);
      setLoyaltyVoucher(null);
      setVoucherInput("");
      setAppliedVoucher(null);
      setVoucherError("");
      setVoucherMessage("");
      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        const data = await getProfile();
        if (cancelled) return;
        setProfile(data);
        setContactName((prev) => (prev ? prev : data?.name ?? ""));
        setContactEmail((prev) => (prev ? prev : data?.email ?? ""));
        setContactPhone((prev) => (prev ? prev : data?.phone ?? ""));
      } catch {
        if (!cancelled) {
          setProfile(null);
          setLoyaltyVoucher(null);
          setVoucherInput("");
          setAppliedVoucher(null);
          setVoucherError("");
          setVoucherMessage("");
        }
        return;
      }

      try {
        const status = await getRewardStatus();
        if (cancelled) return;
        const voucher = normalizeRewardVoucher(status?.activeVoucher ?? null);
        setLoyaltyVoucher(voucher);
        if (voucher && !voucher.redeemed) {
          setVoucherInput((prev) => prev || voucher.code);
        }
      } catch {
        if (!cancelled) {
          setLoyaltyVoucher(null);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

	  useEffect(() => {
	    if (!selectedLocationId) {
	      setLocationEquipmentByTypeId(null);
	      setLocationEquipmentLoading(false);
	      setLocationEquipmentError("");
	      return;
	    }

    let cancelled = false;
    setLocationEquipmentLoading(true);
    setLocationEquipmentError("");

	    getLocationEquipment(selectedLocationId)
	      .then((records) => {
	        if (cancelled) return;
	        setLocationEquipmentByTypeId(buildEquipmentQuantityLookup(records));
	      })
	      .catch((err) => {
	        if (cancelled) return;
	        setLocationEquipmentByTypeId(null);
	        setLocationEquipmentError(
	          err?.message ??
	          t(
	            "bookingDay.equipment.error",
            "We couldn’t load equipment for this location."
          )
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLocationEquipmentLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedLocationId, t]);

  const availabilityRequest = useMemo(() => {
    if (!requiresAvailability || !startIso || !endIso) {
      return null;
    }

    const itemsKey = bookingItemsPayload
      .map((item) => `${item.equipmentTypeId}:${item.quantity}`)
      .join("|");

    return {
      key: `${selectedLocationId}|${startIso}|${endIso}|${itemsKey}`,
      payload: {
        locationId: selectedLocationId,
        startUtc: startIso,
        endUtc: endIso,
        items: bookingItemsPayload,
      },
    };
  }, [bookingItemsPayload, endIso, requiresAvailability, selectedLocationId, startIso]);

  useEffect(() => {
    if (!availabilityRequest) {
      setAvailabilityResult(null);
      setAvailabilityError("");
      setAvailabilityLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setAvailabilityError("");
      setAvailabilityLoading(true);

      checkAvailability(availabilityRequest.payload)
        .then((result) => {
          if (!cancelled) {
            setAvailabilityResult(result);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setAvailabilityResult(null);
            setAvailabilityError(
              err?.message ??
              t(
                "bookingDay.availability.error",
                "We couldn’t verify availability. Please try again."
              )
            );
          }
        })
        .finally(() => {
          if (!cancelled) {
            setAvailabilityLoading(false);
          }
        });
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [availabilityRequest, t]);

  const ensureContactInfo = () => {
    const email = contactEmail.trim();
    const phone = contactPhone.trim();

    if (!email || !phone) {
      setContactError(
        t(
          "bookingDay.payment.contact.missing",
          "Please enter your email and phone number to continue."
        )
      );
      return false;
    }

    if (!isValidEmail(email)) {
      setContactError(
        t(
          "bookingDay.payment.contact.invalidEmail",
          "Please enter a valid email address."
        )
      );
      return false;
    }

    if (!isValidE164(phone)) {
      setContactError(
        t(
          "bookingDay.payment.contact.invalidPhone",
          "Please enter a valid phone number in international format (e.g., +43123456789)."
        )
      );
      return false;
    }
    setContactError("");
    return true;
  };

  const isContactIncomplete = !contactEmail.trim() || !contactPhone.trim();
  const isContactInvalid =
    (contactEmail.trim() && !isValidEmail(contactEmail)) ||
    (contactPhone.trim() && !isValidE164(contactPhone));
  const canSubmitPayment = isEventCheckout ? true : canSubmitBooking;
  const isPayDisabled = isPaying || isContactIncomplete || isContactInvalid || !canSubmitPayment;
  const hideNavActions = isEventCheckout || (step === 3 && (isPaying || paySuccess));
  const submitLockRef = useRef(false);
  const finalizeLockRef = useRef(false);

  useEffect(() => {
    if (!isPaying || paySuccess || payFailed) {
      setPayStuck(false);
      return;
    }

    const timer = setTimeout(
      () => setPayStuck(true),
      payPendingRedirect ? 45000 : 20000
    );
    return () => clearTimeout(timer);
  }, [isPaying, payFailed, payPendingRedirect, paySuccess]);

  const handleResetPayment = () => {
    clearPendingBooking();
    submitLockRef.current = false;
    finalizeLockRef.current = false;
    setPayStuck(false);
    setPayPendingRedirect(false);
    setIsPaying(false);
    setPayFailed(false);
    setPaySuccess(false);
    setPayError(null);
    navigate(window.location.pathname, { replace: true });
    setStep(0);
  };

  // modified goToStep
  const goToStep = (newStep) => {
    if (newStep > step && !canProceed()) return; // 🚫 stop if invalid
    setTransitionDirection(newStep > step ? "slide-right" : "slide-left");
    setStep(newStep);
  };


  // === PRICE MODEL ===
  const isEquipmentSelectable = useCallback(
    (key) => {
      if (!locationEquipmentByTypeId) {
        return true;
      }
      const max = getMaxQuantityForKey(key, EQUIPMENT_KEY_TO_ID, locationEquipmentByTypeId);
      return typeof max === "number" && max > 0;
    },
    [locationEquipmentByTypeId]
  );

  const handleItemChange = (type, delta) => {
    if (!isEquipmentSelectable(type)) {
      return;
    }
    const max = getMaxQuantityForKey(type, EQUIPMENT_KEY_TO_ID, locationEquipmentByTypeId);
    setItems((prev) => {
      const current = prev[type] ?? 0;
      const next = Math.max(0, current + delta);
      const clamped = max === null ? next : Math.min(next, max);
      if (clamped === current) return prev;
      return { ...prev, [type]: clamped };
    });
  };

  const calcItemSubtotal = (key, quantity, dur) => {
    if (!quantity) return 0;
    const tripPrice = tripPrices[key];
    const dayPrice = dayPrices[key];
    const weeklyPrice = weeklyPrices[key];

    if (typeof tripPrice === "number") {
      return quantity * tripPrice;
    }

    const chargeableDays = Math.max(1, Math.ceil(dur / 24));
    const weeks = Math.floor(chargeableDays / 7);
    const remainingDays = chargeableDays % 7;

    let subtotal = 0;
    if (weeks > 0 && typeof weeklyPrice === "number") {
      subtotal += weeks * weeklyPrice;
    }
    if (remainingDays > 0 && typeof dayPrice === "number") {
      subtotal += remainingDays * dayPrice;
    }

    return quantity * subtotal;
  };

  const calcTotalForItems = (itemsSnapshot, dur) =>
    DAY_EQUIPMENT_KEYS.reduce(
      (total, key) => total + calcItemSubtotal(key, itemsSnapshot[key] ?? 0, dur),
      0
    );

  const calcTotalPrice = (dur) => calcTotalForItems(items, dur);

  const eventTotalPrice = eventCheckout ? Number(eventCheckout.totalPrice.toFixed(2)) : 0;
  const totalPrice = isEventCheckout ? eventTotalPrice : calcTotalPrice(durationHours);
  const availableVoucher = useMemo(
    () => (loyaltyVoucher && !loyaltyVoucher.redeemed ? loyaltyVoucher : null),
    [loyaltyVoucher]
  );
  const eligibleVoucherAmount = useMemo(() => {
    if (totalPrice <= 0) return 0;
    return Math.min(REWARD_VOUCHER_DISCOUNT_EUR, Number(totalPrice.toFixed(2)));
  }, [totalPrice]);

  useEffect(() => {
    if (!availableVoucher) return;
    setVoucherInput((prev) => prev || availableVoucher.code);
  }, [availableVoucher]);

  useEffect(() => {
    if (!appliedVoucher) return;

    if (appliedVoucher.kind === "reward") {
      if (eligibleVoucherAmount <= 0) {
        setAppliedVoucher(null);
        setVoucherMessage("");
        setVoucherError(t("bookingDay.voucher.invalid", "This code isn't valid."));
        return;
      }

      const nextAmount = Math.min(eligibleVoucherAmount, Number(totalPrice.toFixed(2)));
      if (appliedVoucher.amount !== nextAmount) {
        setAppliedVoucher((prev) => (prev ? { ...prev, amount: nextAmount } : null));
      }
      return;
    }

    if (appliedVoucher.kind === "promo") {
      const { promoPercentRate, promoFixedAmount } = appliedVoucher;
      if (promoPercentRate === null && promoFixedAmount === null) {
        return;
      }
      if (totalPrice <= 0) {
        return;
      }
      const rawAmount =
        typeof promoPercentRate === "number" && promoPercentRate > 0
          ? totalPrice * promoPercentRate
          : promoFixedAmount;
      const nextAmount = Math.min(
        Number((Number.isFinite(rawAmount) ? rawAmount : 0).toFixed(2)),
        Number(totalPrice.toFixed(2))
      );
      if (nextAmount <= 0) {
        setAppliedVoucher(null);
        setVoucherMessage("");
        setVoucherError(t("bookingDay.voucher.invalid", "This code isn't valid."));
        return;
      }
      if (appliedVoucher.amount !== nextAmount) {
        setAppliedVoucher((prev) => (prev ? { ...prev, amount: nextAmount } : null));
      }
    }
  }, [
    appliedVoucher,
    eligibleVoucherAmount,
    t,
    totalPrice,
  ]);

  const voucherDiscount =
    Number.isFinite(appliedVoucher?.amount) && appliedVoucher.amount > 0
      ? Math.min(appliedVoucher.amount, Number(totalPrice.toFixed(2)))
      : 0;
  const payableTotal = Math.max(0, Number((totalPrice - voucherDiscount).toFixed(2)));
  const paymentAmount = Number(payableTotal.toFixed(2));
  const shouldUseStripe = paymentAmount > 0;
  const displayPricingSummary = useMemo(
    () =>
      getPricingDisplaySummary({
        liveBaseTotal: totalPrice,
        liveFinalTotal: payableTotal,
        liveVoucher: appliedVoucher,
        persistedSummary: persistedPricingSummary,
        preferPersisted: paySuccess || payPendingRedirect,
      }),
    [
      appliedVoucher,
      payPendingRedirect,
      paySuccess,
      payableTotal,
      persistedPricingSummary,
      totalPrice,
    ]
  );

  const buildBookingPayload = useCallback(() => {
    if (!selectedLocationId || !startIso || !endIso || !bookingItemsPayload.length) {
      return null;
    }

    return {
      type: BOOKING_TYPE.DAY,
      locationId: selectedLocationId,
      startUtc: startIso,
      endUtc: endIso,
      items: bookingItemsPayload,
      contactName: contactName.trim() || null,
      contactEmail: contactEmail.trim(),
      contactPhone: contactPhone.trim(),
      rewardVoucherCode:
        appliedVoucher?.kind === "reward" ? appliedVoucher.code ?? null : null,
      promoCode: appliedVoucher?.kind === "promo" ? appliedVoucher.code ?? null : null,
    };
  }, [
    appliedVoucher,
    bookingItemsPayload,
    contactEmail,
    contactName,
    contactPhone,
    endIso,
    selectedLocationId,
    startIso,
  ]);

  useEffect(() => {
    if (isEventCheckout) {
      if (step !== 3 || !shouldUseStripe || redirectIntentId || redirectStatus) {
        setPaymentIntent(null);
        setPaymentIntentError("");
        setPaymentIntentLoading(false);
        return;
      }

      if (paymentIntent && Number(paymentIntent.amount) === paymentAmount) {
        return;
      }

      let cancelled = false;
      setPaymentIntentLoading(true);
      setPaymentIntentError("");

      createEventPaymentIntent({
        eventId: eventCheckout.id,
        eventTitle: eventCheckout.title,
        startUtc: eventCheckout.startDate.toISOString(),
        endUtc: eventCheckout.endDate.toISOString(),
        amount: paymentAmount,
        currency: "EUR",
        method: "card",
        quantity: eventCheckout.quantity,
        equipmentItems: eventCheckout.equipmentItems,
      })
        .then((intent) => {
          if (cancelled) return;
          if (!intent?.clientSecret) {
            setPaymentIntent(null);
            setPaymentIntentError(
              t(
                "bookingDay.payment.configError",
                "Stripe payments are not configured yet."
              )
            );
            return;
          }
          setPaymentIntent(intent);
        })
        .catch((err) => {
          if (cancelled) return;
          setPaymentIntent(null);
          setPaymentIntentError(
            err?.message ??
            t(
              "bookingDay.payment.error",
              "We couldn’t finalize your booking. Please try again."
            )
          );
        })
        .finally(() => {
          if (!cancelled) {
            setPaymentIntentLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
    }

    if (step !== 3 || !shouldUseStripe || redirectIntentId || redirectStatus) {
      setPaymentIntent(null);
      setPaymentIntentError("");
      setPaymentIntentLoading(false);
      return;
    }

    if (paymentIntent && Number(paymentIntent.amount) === paymentAmount) {
      return;
    }

    let cancelled = false;
    setPaymentIntentLoading(true);
    setPaymentIntentError("");

    const paymentPayload = buildBookingPayload();
    if (!paymentPayload) {
      setPaymentIntent(null);
      setPaymentIntentLoading(false);
      return undefined;
    }

    createPaymentIntent({
      ...paymentPayload,
      amount: 0,
      currency: "EUR",
      method: "card",
    })
      .then((intent) => {
        if (cancelled) return;
        if (!intent?.clientSecret) {
          setPaymentIntent(null);
          setPaymentIntentError(
            t(
              "bookingDay.payment.configError",
              "Stripe payments are not configured yet."
            )
          );
          return;
        }
        setPaymentIntent(intent);
      })
      .catch((err) => {
        if (cancelled) return;
        setPaymentIntent(null);
        setPaymentIntentError(
          err?.message ??
          t(
            "bookingDay.payment.error",
            "We couldn’t finalize your booking. Please try again."
          )
        );
      })
      .finally(() => {
        if (!cancelled) {
          setPaymentIntentLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    buildBookingPayload,
    eventCheckout,
    isEventCheckout,
    paymentAmount,
    paymentIntent,
    redirectIntentId,
    redirectStatus,
    shouldUseStripe,
    step,
    t,
  ]);

  const stripePromise = step === 3 && shouldUseStripe ? getStripePromise() : null;

  useEffect(() => {
    const pending = loadPendingBooking();
    const shouldFinalizeFromRedirect = Boolean(redirectStatus || redirectIntentId);
    const shouldFinalizeFromPending =
      !shouldFinalizeFromRedirect &&
      pending?.paymentIntentId &&
      (pending?.awaitingRedirect || pending?.status === "pending");
    const pendingAgeMs = pending?.createdUtc
      ? Date.now() - new Date(pending.createdUtc).getTime()
      : null;
    const isPendingStale =
      typeof pendingAgeMs === "number" && pendingAgeMs > PENDING_REDIRECT_TIMEOUT_MS;

    const releaseFinalizeLock = () => {
      finalizeLockRef.current = false;
    };

    if (!shouldFinalizeFromRedirect && !shouldFinalizeFromPending) {
      setPayPendingRedirect(false);
      releaseFinalizeLock();
      return;
    }

    if (pending?.payload?.type && pending.payload.type !== BOOKING_TYPE.DAY) {
      const targetPath = "/booking-day";
      if (typeof window !== "undefined" && window.location.pathname !== targetPath) {
        const search = window.location.search || "";
        const hash = window.location.hash || "";
        navigate(`${targetPath}${search}${hash}`, { replace: true });
        return;
      }
    }

    if (finalizeLockRef.current) {
      return;
    }

    finalizeLockRef.current = true;

    setStep(3);
    setIsPaying(true);
    setPayPendingRedirect(true);
    setPayError(null);
    setPayFailed(false);

    if (shouldFinalizeFromPending && isPendingStale) {
      clearPendingBooking();
      setPayError(
        t(
          "bookingDay.payment.expired",
          "This payment session expired. Please start a new booking."
        )
      );
      setPayFailed(true);
      setPaySuccess(false);
      setPayPendingRedirect(false);
      setIsPaying(false);
      releaseFinalizeLock();
      return;
    }

    if (!pending?.payload) {
      clearPendingBooking();
      if (redirectStatus === "succeeded") {
        setPayError(null);
        setPayFailed(false);
        setPaySuccess(true);
        setPayPendingRedirect(false);
        setIsPaying(false);
        releaseFinalizeLock();
        return;
      }
      setPayError(
        t("bookingDay.payment.unsuccessful", "Payment could not be completed. Please try again.")
      );
      setPayFailed(true);
      setPaySuccess(false);
      setPayPendingRedirect(false);
      setIsPaying(false);
      navigate(window.location.pathname, { replace: true });
      releaseFinalizeLock();
      return;
    }

    const expectedIntentId = pending.paymentIntentId ?? null;
    const resolvedIntentId = redirectIntentId || expectedIntentId;
    const payload = pending.payload;
    const restoredPricingSummary = pending.pricingSummary ?? null;
    if (pending.kind === "event" && pending.eventCheckout) {
      setRestoredEventCheckout(normalizeEventCheckout(pending.eventCheckout));
    }
    setPersistedPricingSummary(restoredPricingSummary);
    if (payload?.startUtc && payload?.endUtc) {
      const startDate = new Date(payload.startUtc);
      const endDate = new Date(payload.endUtc);
      if (!Number.isNaN(startDate.getTime())) {
        setRangeStartDate(startDate);
      }
      if (!Number.isNaN(endDate.getTime())) {
        setRangeEndDate(endDate);
      }
    }
    if (payload?.contactEmail) {
      setContactEmail(payload.contactEmail);
    }
    if (payload?.contactPhone) {
      setContactPhone(payload.contactPhone);
    }
    if (payload?.contactName) {
      setContactName(payload.contactName);
    }
    if (payload?.locationId) {
      setSelectedLocationId(payload.locationId);
    }
    if (Array.isArray(payload?.items)) {
      const nextItems = createEmptyItems();
      payload.items.forEach((item) => {
        const key = DAY_EQUIPMENT_KEYS.find(
          (equipmentKey) =>
            normalizeGuid(EQUIPMENT_KEY_TO_ID[equipmentKey]) ===
            normalizeGuid(item.equipmentTypeId)
        );
        if (key) {
          nextItems[key] = (nextItems[key] ?? 0) + item.quantity;
        }
      });
      setItems(nextItems);

      const restoredVoucher = cloneAppliedVoucher(restoredPricingSummary?.voucher);
      if (restoredVoucher) {
        setAppliedVoucher(restoredVoucher);
        setVoucherInput((prev) => prev || restoredVoucher.code);
      } else {
        const rewardCode = payload.rewardVoucherCode?.trim().toUpperCase();
        if (rewardCode) {
          setAppliedVoucher({ kind: "reward", code: rewardCode, amount: 0 });
          setVoucherInput((prev) => prev || rewardCode);
        }

        const promoCode = payload.promoCode?.trim().toUpperCase();
        if (promoCode) {
          setAppliedVoucher({
            kind: "promo",
            code: promoCode,
            amount: 0,
            promoPercentRate: null,
            promoFixedAmount: null,
          });
          setVoucherInput((prev) => prev || promoCode);
        }
      }
    }

    if (redirectStatus && redirectStatus !== "succeeded") {
      clearPendingBooking();
      setPayError(
        t("bookingDay.payment.unsuccessful", "Payment could not be completed. Please try again.")
      );
      setPayFailed(true);
      setPaySuccess(false);
      setIsPaying(false);
      navigate(window.location.pathname, { replace: true });
      releaseFinalizeLock();
      return;
    }

    if (!resolvedIntentId) {
      clearPendingBooking();
      setPayError(t("bookingDay.payment.missingIntent", "Missing Stripe payment intent."));
      setPayFailed(true);
      setPaySuccess(false);
      setPayPendingRedirect(false);
      setIsPaying(false);
      navigate(window.location.pathname, { replace: true });
      releaseFinalizeLock();
      return;
    }

    if (redirectIntentId && expectedIntentId && expectedIntentId !== redirectIntentId) {
      clearPendingBooking();
      setPayError(
        t(
          "bookingDay.payment.intentMismatch",
          "Payment intent does not match the pending booking."
        )
      );
      setPayFailed(true);
      setPaySuccess(false);
      setPayPendingRedirect(false);
      setIsPaying(false);
      navigate(window.location.pathname, { replace: true });
      releaseFinalizeLock();
      return;
    }

    if (
      pending.status === "completed" &&
      pending.completedPaymentIntentId === resolvedIntentId
    ) {
      setPersistedPricingSummary(pending.pricingSummary ?? null);
      setLatestBooking(pending.booking ?? null);
      setPaySuccess(true);
      setPayFailed(false);
      setPayPendingRedirect(false);
      setIsPaying(false);
      clearPendingBooking();
      navigate(window.location.pathname, { replace: true });
      releaseFinalizeLock();
      return;
    }

    let isMounted = true;
    const finalize = async () => {
      try {
        const fetchPaymentIntent = async (intentId) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), PAYMENT_INTENT_TIMEOUT_MS);
          try {
            return await getPaymentIntent(intentId, { signal: controller.signal });
          } finally {
            clearTimeout(timeoutId);
          }
        };

        const attemptCreateBooking = async () => {
          const bookingPayload = {
            ...pending.payload,
            paymentIntentId: resolvedIntentId,
          };
          if (pending.kind === "event") {
            return pending.isAuthenticated
              ? await createEventBooking(bookingPayload)
              : await createGuestEventBooking(bookingPayload);
          }
          return pending.isAuthenticated
            ? await createBooking(bookingPayload)
            : await createGuestBooking(bookingPayload);
        };

        const pollPaymentStatus = async (intentId) => {
          const maxAttempts = 4;
          const delayMs = 1500;
          let lastPayment = null;

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const nextPayment = await fetchPaymentIntent(intentId);
            lastPayment = nextPayment;
            const status = String(nextPayment?.status ?? "").toLowerCase();
            if (status === "succeeded") {
              return status;
            }
            if (status !== "processing" && status !== "pending") {
              return status;
            }
            if (attempt < maxAttempts - 1) {
              await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
          }

          return String(lastPayment?.status ?? "").toLowerCase();
        };

        let created;
        try {
          created = await attemptCreateBooking();
        } catch (err) {
          const message = String(err?.message ?? "");
          if (!message.includes("Payment is not completed")) {
            throw err;
          }

          const status = await pollPaymentStatus(resolvedIntentId);
          if (status !== "succeeded") {
            if (status === "processing" || status === "pending") {
              throw new Error(
                t(
                  "bookingDay.payment.stillProcessing",
                  "Payment is still processing. Please check back in a moment."
                )
              );
            }
            throw new Error(t("bookingDay.payment.notCompleted", "Payment has not completed."));
          }

          created = await attemptCreateBooking();
        }

        if (!isMounted) return;
        setLatestBooking(created);
        setPaySuccess(true);
        setPayFailed(false);
        setPayPendingRedirect(false);
        savePendingBooking({
          ...pending,
          status: "completed",
          completedPaymentIntentId: resolvedIntentId,
          booking: created,
          awaitingRedirect: false,
        });
        clearPendingBooking();
      } catch (err) {
        if (!isMounted) return;
        updatePendingBooking((prev) =>
          prev ? { ...prev, awaitingRedirect: false, status: "failed" } : prev
        );
        setPayError(
          err?.message ??
          t(
            "bookingDay.payment.error",
            "We couldn’t finalize your booking. Please try again."
          )
        );
        setPayFailed(true);
        setPayPendingRedirect(false);
      } finally {
        if (isMounted) {
          setPayPendingRedirect(false);
          setIsPaying(false);
          navigate(window.location.pathname, { replace: true });
        }
        releaseFinalizeLock();
      }
    };

    finalize();
    return () => {
      isMounted = false;
      releaseFinalizeLock();
    };
  }, [
    redirectStatus,
    redirectIntentId,
    navigate,
    t,
    createEmptyItems,
  ]);

  const handleApplyVoucher = async () => {
    setVoucherError("");
    setVoucherMessage("");

    const normalizedInput = voucherInput.trim().toUpperCase();
    if (!normalizedInput) {
      setVoucherError(
        t("bookingDay.voucher.enterCode", "Enter a voucher code.")
      );
      return;
    }

    setVoucherValidating(true);

    let promoDiscount = resolvePromoDiscountFromPricing(pricingSettings, normalizedInput);
    let latestPricing = pricingSettings;
    if (!promoDiscount) {
      try {
        latestPricing = await getPublicPricingSettings();
        promoDiscount = resolvePromoDiscountFromPricing(latestPricing, normalizedInput);
      } catch {
        promoDiscount = null;
      }
    }

    if (promoDiscount) {
      const rawAmount =
        typeof promoDiscount.percentRate === "number" && promoDiscount.percentRate > 0
          ? totalPrice * promoDiscount.percentRate
          : promoDiscount.fixedAmount;
      const discountAmount = Math.min(
        Number((Number.isFinite(rawAmount) ? rawAmount : 0).toFixed(2)),
        Number(totalPrice.toFixed(2))
      );
      if (discountAmount <= 0) {
        setVoucherError(t("bookingDay.voucher.invalid", "This code isn't valid."));
        setVoucherValidating(false);
        return;
      }
      setAppliedVoucher({
        kind: "promo",
        code: promoDiscount.code,
        amount: discountAmount,
        promoPercentRate: promoDiscount.percentRate ?? null,
        promoFixedAmount: promoDiscount.fixedAmount ?? null,
      });
      setVoucherMessage(t("bookingDay.voucher.promoApplied", "Promo applied!"));
      setVoucherValidating(false);
      return;
    }

    const manualPromo = findManualPromoByCode(latestPricing, normalizedInput);
    if (manualPromo) {
      setVoucherError(
        isManualPromoExhausted(manualPromo)
          ? t("bookingDay.voucher.promoRedeemed", "This promo code has already been redeemed.")
          : t("bookingDay.voucher.promoUnavailable", "This promo code is no longer available.")
      );
      setVoucherValidating(false);
      return;
    }

    const looksLikeRewardVoucher = normalizedInput.startsWith("WAYLI-FREE-");
    if (!looksLikeRewardVoucher) {
      setVoucherError(t("bookingDay.voucher.invalid", "This code isn't valid."));
      setVoucherValidating(false);
      return;
    }

    if (!isAuthenticated) {
      setVoucherError(
        t("bookingDay.voucher.authRequired", "Please log in to use a reward voucher.")
      );
      setVoucherValidating(false);
      return;
    }

    if (eligibleVoucherAmount <= 0) {
      setVoucherError(
        t(
          "bookingDay.voucher.checkFailed",
          "We couldn't validate that voucher. Please try again."
        )
      );
      setVoucherValidating(false);
      return;
    }

    try {
      await validateRewardVoucher(normalizedInput);
      const discountAmount = Math.min(eligibleVoucherAmount, Number(totalPrice.toFixed(2)));
      setAppliedVoucher({ kind: "reward", code: normalizedInput, amount: discountAmount });
      setVoucherMessage(
        t("bookingDay.voucher.applied", "Reward applied! €20 discount added.")
      );
    } catch (err) {
      const status = err?.status;
      if (status === 409) {
        setVoucherError(
          t("bookingDay.voucher.redeemed", "This voucher has already been redeemed.")
        );
      } else if (status === 404) {
        setVoucherError(
          t("bookingDay.voucher.invalid", "This code isn't valid.")
        );
      } else {
        setVoucherError(
          t(
            "bookingDay.voucher.checkFailed",
            "We couldn't validate that voucher. Please try again."
          )
        );
      }
    } finally {
      setVoucherValidating(false);
    }
  };

  const handleRemoveVoucher = () => {
    setAppliedVoucher(null);
    setVoucherMessage("");
    setVoucherError("");
  };

  const handleDateTimeSelection = ({ startDate, endDate }) => {
    if (startDate instanceof Date && !Number.isNaN(startDate.getTime())) {
      setRangeStartDate(startDate);
    }
    if (endDate == null) {
      setRangeEndDate(null);
    } else if (endDate instanceof Date && !Number.isNaN(endDate.getTime())) {
      setRangeEndDate(endDate);
    }
  };

  const validateBeforePay = () => {
    if (!ensureContactInfo()) return false;

    if (!requiresAvailability) {
      if (startUtc && endUtc && hasBlockedPickupOrReturn) {
        setPayError(
          t(
            "bookingDay.validation.blockedPickupReturn",
            "Pickup or return is not available on one of the selected dates."
          )
        );
        return false;
      }

      setPayError(
        t(
          "bookingDay.payment.incomplete",
          "Please select a location, time, and equipment before paying."
        )
      );
      return false;
    }

    if (startUtc && endUtc && !isTimeSlotValid) {
      setPayError(
        t(
          "bookingDay.validation.outsideHours",
          "Please choose a valid rental range within this location’s available hours."
        )
      );
      return false;
    }

    if (availabilityLoading) {
      setPayError(
        t(
          "bookingDay.payment.checkingAvailability",
          "Hang tight, we’re confirming availability."
        )
      );
      return false;
    }

    if (availabilityResult && !availabilityResult.isAvailable) {
      setPayError(unavailableDateSummary);
      return false;
    }

    const payload = buildBookingPayload();
    if (!payload) {
      setPayError(
        t(
          "bookingDay.payment.incomplete",
          "Please select a location, time, and equipment before paying."
        )
      );
      return false;
    }

    return payload;
  };

  const ensureAppliedPromoStillValid = async () => {
    if (appliedVoucher?.kind !== "promo") return true;
    const code = appliedVoucher.code;
    if (!code) return true;
    try {
      const latestPricing = await getPublicPricingSettings();
      const promoDiscount = resolvePromoDiscountFromPricing(latestPricing, code);
      if (!promoDiscount) {
        const promo = findManualPromoByCode(latestPricing, code);
        setAppliedVoucher(null);
        setVoucherMessage("");
        setVoucherError(
          promo && isManualPromoExhausted(promo)
            ? t(
              "bookingDay.voucher.promoRedeemed",
              "This promo code has already been redeemed."
            )
            : t(
              "bookingDay.voucher.promoUnavailable",
              "This promo code is no longer available."
            )
        );
        return false;
      }
      return true;
    } catch {
      setVoucherError(
        t(
          "bookingDay.voucher.checkFailed",
          "We couldn't validate that voucher. Please try again."
        )
      );
      return false;
    }
  };

  const handleFreeBookingSubmit = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    const payload = validateBeforePay();
    if (!payload) {
      submitLockRef.current = false;
      return;
    }

    setIsPaying(true);
    setPayFailed(false);
    setPayPendingRedirect(false);
    setPayError(null);
    setContactError("");

    try {
      if (!(await ensureAppliedPromoStillValid())) {
        return;
      }

      setPersistedPricingSummary(
        buildPersistedPricingSummary({
          baseTotal: totalPrice,
          finalTotal: paymentAmount,
          appliedVoucher,
        })
      );

      const payment = await createPaymentIntent({
        ...payload,
        amount: 0,
        currency: "EUR",
        method: "card",
      });

      if (!payment || String(payment.status).toLowerCase() !== "succeeded") {
        setPayError(
          t(
            "bookingDay.payment.unsuccessful",
            "Payment could not be completed. Please try again."
          )
        );
        setPayFailed(true);
        return;
      }

      const bookingPayload = { ...payload, paymentIntentId: payment.id };
      const booking = isAuthenticated
        ? await createBooking(bookingPayload)
        : await createGuestBooking(bookingPayload);
      setLatestBooking(booking);
      if (appliedVoucher?.kind === "reward" && isAuthenticated) {
        setLoyaltyVoucher((prev) =>
          prev
            ? { ...prev, redeemed: true, redeemedUtc: new Date().toISOString() }
            : prev
        );
      }
      clearPendingBooking();
      setPaySuccess(true);
      setPayFailed(false);
    } catch (err) {
      setPayError(
        err?.message ??
        t(
          "bookingDay.payment.error",
          "We couldn’t finalize your booking. Please try again."
        )
      );
      setPayFailed(true);
    } finally {
      setIsPaying(false);
      submitLockRef.current = false;
    }
  };

  const handleStripeBookingSubmit = async (stripe, elements) => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    if (isEventCheckout) {
      if (!ensureContactInfo()) {
        submitLockRef.current = false;
        return;
      }

      if (!paymentIntent?.clientSecret) {
        setPayError(
          t(
            "bookingDay.payment.error",
            "We couldn’t initialize the payment. Please try again."
          )
        );
        submitLockRef.current = false;
        return;
      }

      setIsPaying(true);
      setPayFailed(false);
      setPayPendingRedirect(true);
      setPayError(null);
      setContactError("");

      try {
        const eventBookingPayload = {
          eventId: eventCheckout.id,
          contactName: contactName.trim(),
          contactEmail: contactEmail.trim(),
          contactPhone: contactPhone.trim(),
          startUtc: eventCheckout.startDate.toISOString(),
          endUtc: eventCheckout.endDate.toISOString(),
          quantity: eventCheckout.quantity,
          equipmentItems: eventCheckout.equipmentItems,
        };

        savePendingBooking({
          kind: "event",
          payload: eventBookingPayload,
          eventCheckout: buildEventCheckoutSnapshot(eventCheckout),
          isAuthenticated,
          amount: paymentAmount,
          currency: eventCheckout.currency || "EUR",
          pricingSummary: buildPersistedPricingSummary({
            baseTotal: totalPrice,
            finalTotal: paymentAmount,
            appliedVoucher: null,
          }),
          paymentIntentId: paymentIntent.id ?? null,
          status: "pending",
          awaitingRedirect: true,
          createdUtc: new Date().toISOString(),
        });

        const { error, paymentIntent: confirmedIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: buildReturnUrl(),
            payment_method_data: {
              billing_details: {
                email: contactEmail.trim(),
                phone: contactPhone.trim(),
                name: contactName.trim() || undefined,
              },
            },
          },
          redirect: "if_required",
        });

        if (error) {
          updatePendingBooking((prev) =>
            prev ? { ...prev, awaitingRedirect: false, status: "failed" } : prev
          );
          if (isStripeInlineValidationError(error)) {
            setPayPendingRedirect(false);
            return {
              inlineError:
                error.message ??
                t(
                  "bookingDay.payment.cardForm.incomplete",
                  "Please complete your card details before paying."
                ),
            };
          }
          setPayError(
            error.message ??
            t(
              "bookingDay.payment.error",
              "We couldn’t finalize your booking. Please try again."
            )
          );
          setPayFailed(true);
          setPayPendingRedirect(false);
          return;
        }

        if (!confirmedIntent || confirmedIntent.status !== "succeeded") {
          updatePendingBooking((prev) =>
            prev ? { ...prev, awaitingRedirect: false, status: "failed" } : prev
          );
          setPayError(
            t(
              "bookingDay.payment.unsuccessful",
              "Payment could not be completed. Please try again."
            )
          );
          setPayFailed(true);
          setPayPendingRedirect(false);
          return;
        }

        const completedEventBookingPayload = {
          ...eventBookingPayload,
          paymentIntentId: confirmedIntent.id ?? paymentIntent.id,
        };
        const booking = isAuthenticated
          ? await createEventBooking(completedEventBookingPayload)
          : await createGuestEventBooking(completedEventBookingPayload);
        setLatestBooking(booking);
        clearPendingBooking();
        setPaySuccess(true);
        setPayFailed(false);
        setPayPendingRedirect(false);
      } catch (err) {
        setPayError(
          err?.message ??
          t(
            "bookingDay.payment.error",
            "We couldn’t finalize your booking. Please try again."
          )
        );
        setPayFailed(true);
        setPayPendingRedirect(false);
      } finally {
        setPayPendingRedirect(false);
        setIsPaying(false);
        submitLockRef.current = false;
      }
      return;
    }

    const payload = validateBeforePay();
    if (!payload) {
      submitLockRef.current = false;
      return;
    }

    if (!paymentIntent?.clientSecret) {
      setPayError(
        t(
          "bookingDay.payment.error",
          "We couldn’t initialize the payment. Please try again."
        )
      );
      submitLockRef.current = false;
      return;
    }

    const returnUrl = buildReturnUrl();

    setIsPaying(true);
    setPayFailed(false);
    setPayPendingRedirect(true);
    setPayError(null);
    setContactError("");

    try {
      if (!(await ensureAppliedPromoStillValid())) {
        return;
      }

      const pricingSummary = buildPersistedPricingSummary({
        baseTotal: totalPrice,
        finalTotal: paymentAmount,
        appliedVoucher,
      });
      setPersistedPricingSummary(pricingSummary);

      savePendingBooking({
        payload,
        isAuthenticated,
        amount: paymentAmount,
        currency: "EUR",
        pricingSummary,
        paymentIntentId: paymentIntent.id ?? null,
        status: "pending",
        awaitingRedirect: true,
        createdUtc: new Date().toISOString(),
      });

      const { error, paymentIntent: confirmedIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              email: contactEmail.trim(),
              phone: contactPhone.trim(),
              name: contactName.trim() || undefined,
            },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        updatePendingBooking((prev) =>
          prev ? { ...prev, awaitingRedirect: false, status: "failed" } : prev
        );
        if (isStripeInlineValidationError(error)) {
          setPayPendingRedirect(false);
          return {
            inlineError:
              error.message ??
              t(
                "bookingDay.payment.cardForm.incomplete",
                "Please complete your card details before paying."
              ),
          };
        }
        setPayError(
          error.message ??
          t(
            "bookingDay.payment.error",
            "We couldn’t finalize your booking. Please try again."
          )
        );
        setPayFailed(true);
        setPayPendingRedirect(false);
        return;
      }

      if (!confirmedIntent || confirmedIntent.status !== "succeeded") {
        updatePendingBooking((prev) =>
          prev ? { ...prev, awaitingRedirect: false, status: "failed" } : prev
        );
        setPayError(
          t(
            "bookingDay.payment.unsuccessful",
            "Payment could not be completed. Please try again."
          )
        );
        setPayFailed(true);
        setPayPendingRedirect(false);
        return;
      }

      const bookingPayload = {
        ...payload,
        paymentIntentId: confirmedIntent.id ?? paymentIntent.id,
      };
      const booking = isAuthenticated
        ? await createBooking(bookingPayload)
        : await createGuestBooking(bookingPayload);
      setLatestBooking(booking);
      if (appliedVoucher?.kind === "reward" && isAuthenticated) {
        setLoyaltyVoucher((prev) =>
          prev
            ? { ...prev, redeemed: true, redeemedUtc: new Date().toISOString() }
            : prev
        );
      }
      clearPendingBooking();
      setPaySuccess(true);
      setPayFailed(false);
      setPayPendingRedirect(false);
    } catch (err) {
      updatePendingBooking((prev) =>
        prev ? { ...prev, awaitingRedirect: false, status: "failed" } : prev
      );
      setPayError(
        err?.message ??
        t(
          "bookingDay.payment.error",
          "We couldn’t finalize your booking. Please try again."
        )
      );
      setPayFailed(true);
      setPayPendingRedirect(false);
    } finally {
      setPayPendingRedirect(false);
      setIsPaying(false);
      submitLockRef.current = false;
    }
  };

  const handleRetryPayment = () => {
    setPayFailed(false);
    setPayError(null);
    setPersistedPricingSummary(null);
  };

  return (
    <div className="day-booking-page">
      <Header />

      <h1 className="booking-title">
        {isEventCheckout
          ? t("bookingDay.eventCheckout.title", "Book Your Event")
          : t("bookingDay.title", "Book Your Adventure")}
      </h1>

      <div className="booking-form">
        <div className={`carousel-container ${hideNavActions ? "nav-hidden" : ""}`}>
          <div className="carousel-frame">
            <div key={step} className={`carousel-step ${transitionDirection}`}>

              {/* === STEP 0: LOCATION === */}
              {step === 0 && (
                <div className="form-section location-step">
                  <h2>{t("bookingDay.steps.location", "Select Location")}</h2>
                  <div className="location-wrapper">
                    <div className="location-list">
                      {availableDayLocations.map((loc) => (
                        <div
                          key={loc.id}
                          className={`location-item ${selectedLocationId === loc.id ? "active" : ""}`}
                          onClick={() => handleSelectLocation(loc.id)}
                        >
                          <span className="location-dot"></span>
                          <div>
                            <p>{loc.name}</p>
                            {loc.subtitle && <small>{loc.subtitle}</small>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="location-map">
                      <GoogleMapSection
                        locations={availableDayLocations}
                        selectedLocationId={selectedLocationId}
                        onSelectLocation={handleSelectLocation}
                      />
                      {locationsLoading && (
                        <p className="contact-hint">
                          {t("bookingDay.locations.loading", "Loading locations...")}
                        </p>
                      )}
                      {locationsError && (
                        <p className="contact-error" role="alert">
                          {locationsError}
                        </p>
                      )}
                      {!locationsLoading && !availableDayLocations.length && (
                        <p className="contact-hint">
                          {t(
                            "bookingDay.locations.empty",
                            "No active locations are available right now."
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* === STEP 1: EQUIPMENT === */}
              {step === 1 && (
                <div className="form-section">
                  <h2>{t("bookingDay.steps.equipment", "Choose Equipment")}</h2>
                  {!selectedLocationId && (
                    <p className="contact-hint">
                      {t(
                        "bookingDay.equipment.selectLocationPrompt",
                        "Select a base first to see which gear is stocked there."
                      )}
                    </p>
                  )}
                  {locationEquipmentLoading && (
                    <p className="contact-hint">
                      {t("bookingDay.equipment.loading", "Loading equipment...")}
                    </p>
                  )}
                  {!locationEquipmentLoading && locationEquipmentError && (
                    <p className="contact-error" role="alert">
                      {locationEquipmentError}
                    </p>
                  )}
                  {!locationEquipmentLoading && !locationEquipmentError && selectedLocationId &&
                    availableEquipmentKeys.length === 0 && (
                      <p className="contact-error" role="alert">
                        {t(
                          "bookingDay.equipment.empty",
                          "No equipment is available at this location right now."
                        )}
                      </p>
                    )}
                  {(selectedLocationId ? availableEquipmentKeys : DAY_EQUIPMENT_KEYS).map((type, idx) => (
                    <div
                      className={`item-card ${items[type] > 0 ? "active" : ""}`}
                      key={idx}
                      aria-disabled={!isEquipmentSelectable(type)}
                    >
                      <img
                        src={
                          type === "sup"
                            ? "/assets/sup2.png"
                            : type === "big_sup"
                              ? "/assets/sup1.png"
                              : type === "kayak"
                                ? "/assets/kayak.png"
                                : type === "big_kayak"
                                  ? "/assets/kayak_big.png"
                                  : type === "neon_board"
                                    ? "/assets/glow_sup.png"
                                  : "/assets/extras.png"
                        }
                        alt={type}
                      />
                      <div>
                        {(() => {
                          const titleFallback =
                            type === "sup"
                              ? "Solo Glide"
                              : type === "big_sup"
                                ? "Party Board"
                                : type === "kayak"
                                  ? "Small Aqua Arrow"
                                  : type === "big_kayak"
                                    ? "Big Aqua Arrow"
                                    : type === "neon_board"
                                      ? "Neon Glide Board"
                                    : "Kit";
                          const descFallback =
                            type === "sup"
                              ? "Default SUP inkl. Paddle, life vest & drybag"
                              : type === "big_sup"
                                ? "Big SUP ~ 5 people inkl. Paddle, life vest & drybag"
                                : type === "kayak"
                                  ? "1 person kayak inkl. Paddle, life vest & drybag"
                                  : type === "big_kayak"
                                    ? "2 people kayak inkl. Paddle, life vest & drybag"
                                    : type === "neon_board"
                                      ? "LED-lit SUP incl. paddle, life vest & dry bag"
                              : "Towel, waterproof phone case & spf cream";
                        return (
                        <>
                          <h3>
                            {t(`bookingDay.items.${type}.title`, titleFallback)}
                          </h3>
                          <p>
                            {t(
                              `bookingDay.items.${type}.description`,
                              descFallback
                            )}
                          </p>
                          <p className="equipment-price">
                            {getEquipmentPriceLabel(type)}
                          </p>
                        </>
                        );
                        })()}
                      </div>
                      <div className="counter">
                        <button
                          type="button"
                          onClick={() => handleItemChange(type, 1)}
                          disabled={
                            !isEquipmentSelectable(type) ||
                            (getMaxQuantityForKey(
                              type,
                              EQUIPMENT_KEY_TO_ID,
                              locationEquipmentByTypeId
                            ) ?? Infinity) <= (items[type] ?? 0)
                          }
                        >
                          +
                        </button>
                        <span>{items[type]}</span>
                        <button
                          type="button"
                          onClick={() => handleItemChange(type, -1)}
                          disabled={!isEquipmentSelectable(type) || items[type] === 0}
                        >
                          –
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* === STEP 2: DATE & TIME === */}
              {step === 2 && (
                <div className="form-section">
                  <LongTermRangePicker
                    startDate={rangeStartDate}
                    endDate={rangeEndDate}
                    onRangeChange={handleDateTimeSelection}
                    selectableStartDate={selectableCalendarStartDate}
                    selectableEndDate={seasonEndDate}
                    minimumStartDateTime={locationLeadTimeStart}
                    startMinMinutes={selectableStartMinMinutes}
                    startMaxMinutes={selectableStartMaxMinutes}
                    endMinMinutes={selectableEndMinMinutes}
                    endMaxMinutes={selectableEndMaxMinutes}
                    disabledWeekdays={closedWeekdays}
                    blockedDates={blockedDates}
                  />
                  <div
                    className={`availability-panel ${availabilityAlert ? "availability-panel--alert" : ""}`.trim()}
                  >
                    <div className="availability-panel__message">
                      {availabilityLoading && (
                        <p>
                          {t("bookingDay.availability.checking", "Checking availability…")}
                        </p>
                      )}
                      {!availabilityLoading && availabilityError && (
                        <p className="contact-error" role="alert">
                          {availabilityError}
                        </p>
                      )}
                      {!availabilityLoading && !availabilityError && availabilityResult && (
                        <p
                          className={
                            availabilityResult.isAvailable ? "availability-ok" : "contact-error"
                          }
                        >
                          {availabilityResult.isAvailable
                            ? t(
                                "bookingDay.availability.available",
                                "Great news! Your selected gear is available."
                              )
                            : (
                                <>
                                  {unavailableDateSummary}
                                  {unavailableEquipment.length > 0 && (
                                    <span className="availability-tooltip-wrap">
                                      <button
                                        type="button"
                                        className="availability-info"
                                        aria-label={t(
                                          "bookingDay.availability.unavailableInfoLabel",
                                          "Show unavailable gear details"
                                        )}
                                      >
                                        <LuInfo aria-hidden="true" focusable="false" />
                                      </button>
                                      <div
                                        className="availability-tooltip"
                                        role="status"
                                        aria-live="polite"
                                      >
                                        <div className="availability-details__header">
                                          <span className="availability-details__title">
                                            {t(
                                              "bookingDay.availability.unavailableListTitle",
                                              "Unavailable"
                                            )}
                                          </span>
                                          {timeSlotLabel ? (
                                            <span className="availability-details__slot">
                                              {timeSlotLabel}
                                            </span>
                                          ) : null}
                                        </div>
                                        <ul className="availability-details__list">
                                          {unavailableEquipment.map((line) => (
                                            <li
                                              key={line.id}
                                              className="availability-details__item"
                                            >
                                              <span className="availability-details__name">
                                                {line.title}
                                              </span>
                                              <span className="availability-details__counts">
                                                <span className="availability-details__need">
                                                  {t("bookingDay.availability.need", "Need")}{" "}
                                                  {line.requested}
                                                </span>
                                                <span className="availability-details__sep">·</span>
                                                <span className="availability-details__have">
                                                  {t("bookingDay.availability.have", "Avail")}{" "}
                                                  {line.available}
                                                </span>
                                              </span>
                                              {line.unavailableDates.length > 0 && (
                                                <span className="availability-details__counts">
                                                  <span className="availability-details__need">
                                                    {t("bookingDay.availability.unavailableOn", "Blocked on")}{" "}
                                                    {line.unavailableDates
                                                      .map((value) => {
                                                        const parsed = parseDateValue(value);
                                                        return parsed instanceof Date &&
                                                          !Number.isNaN(parsed.getTime())
                                                          ? formatDate(parsed)
                                                          : value;
                                                      })
                                                      .join(", ")}
                                                  </span>
                                                </span>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </span>
                                  )}
                                </>
                              )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* === STEP 3: CONFIRMATION === */}
              {step === 3 && (
                <section className="articles confirmation-card">
                  <article>
                    <div className="checkout">
                      {/* === LEFT: Booking Summary === */}
                      <div className={`summary-pane${isEventCheckout ? " summary-pane--event" : ""}`}>
                        <figure>
                          <img
                            src={isEventCheckout ? eventCheckout.image : "../assets/pos_confirmation.jpg"}
                            alt={
                              isEventCheckout
                                ? eventCheckout.title
                                : t("bookingDay.media.confirmationAlt", "Booking confirmation")
                            }
                            onError={isEventCheckout ? handleEventImageError : undefined}
                          />
                        </figure>

                        <div className="article-body">
                          <h2>
                            {isEventCheckout
                              ? t("bookingDay.eventCheckout.heading", "Your event summary")
                              : t("bookingDay.summary.heading", "Your Booking Summary")}
                          </h2>

                          {isEventCheckout ? (
                            <>
                              <p>
                                <strong>{t("bookingDay.eventCheckout.event", "Event")}:</strong>{" "}
                                {eventCheckout.title}
                              </p>
                              <p>
                                <strong>{t("bookingDay.summary.location", "Location")}:</strong>{" "}
                                {eventCheckout.mapUrl ? (
                                  <a href={eventCheckout.mapUrl} target="_blank" rel="noreferrer">
                                    {eventCheckout.location}
                                  </a>
                                ) : (
                                  eventCheckout.location
                                )}
                              </p>
                              <p>
                                <strong>{t("bookingDay.summary.start", "Start")}:</strong>{" "}
                                {formatDateTimeLabel(eventCheckout.startDate)}
                              </p>
                              <p>
                                <strong>{t("bookingDay.summary.end", "End")}:</strong>{" "}
                                {formatDateTimeLabel(eventCheckout.endDate)}
                              </p>
                              <p>
                                <strong>{t("bookingDay.summary.duration", "Duration")}:</strong>{" "}
                                {eventCheckout.duration}
                              </p>
                              {eventCheckout.isSelfAdjusting && (
                                <p>
                                  <strong>{t("bookingDay.summary.equipment", "Equipment")}:</strong>{" "}
                                  {eventCheckout.equipmentSummary.length
                                    ? eventCheckout.equipmentSummary
                                        .map((item) => `${item.quantity} ${item.name}`)
                                        .join(", ")
                                    : t("bookingDay.eventCheckout.boards", "{count} SUP boards").replace(
                                        "{count}",
                                        String(eventCheckout.quantity)
                                      )}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              <p>
                                <strong>{t("bookingDay.summary.location", "Location")}:</strong>{" "}
                                {selectedLocation?.name ? (
                                  selectedLocationMapUrl ? (
                                    <a
                                      href={selectedLocationMapUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {selectedLocation.name}
                                    </a>
                                  ) : (
                                    selectedLocation.name
                                  )
                                ) : (
                                  t("bookingDay.summary.noneSelected", "Not selected")
                                )}
                              </p>
                              <p>
                                <strong>{t("bookingDay.summary.start", "Start")}:</strong>{" "}
                                {formatDateTimeLabel(rangeStartDate)}
                              </p>
                              <p>
                                <strong>{t("bookingDay.summary.end", "End")}:</strong>{" "}
                                {formatDateTimeLabel(rangeEndDate)}
                              </p>
                              <p>
                                <strong>{t("bookingDay.summary.duration", "Duration")}:</strong>{" "}
                                {durationDaysLabel}
                              </p>
                              <p>
                                <strong>{t("bookingDay.summary.equipment", "Equipment")}:</strong>{" "}
                                {Object.entries(items)
                                  .filter(([_, count]) => count > 0)
                                  .map(
                                    ([type, count]) =>
                                      `${count}× ${t(
                                        `bookingDay.items.${type}.title`,
                                        type.replace("_", " ")
                                      )}`
                                  )
                                  .join(", ") ||
                                  t(
                                    "bookingDay.summary.equipmentFallback",
                                    "No equipment selected"
                                  )}
                              </p>
                            </>
                          )}
                          <p className="total-line">
                            <strong>{t("bookingDay.summary.total", "Total Price")}:</strong> €{" "}
                            {(isEventCheckout
                              ? paymentAmount
                              : displayPricingSummary.finalTotal
                            ).toFixed(2)}
                          </p>
                          {displayPricingSummary.voucher &&
                            displayPricingSummary.voucher.kind === "reward" && (
                            <>
                              <p className="discount-line">
                                {t(
                                  "bookingDay.voucher.discountLine",
                                  "Reward applied: -€{amount}",
                                  {
                                    amount: displayPricingSummary.voucher.amount.toFixed(2),
                                  }
                                )}
                              </p>
                              <p className="original-total">
                                {t(
                                  "bookingDay.voucher.originalTotal",
                                  "Before reward: €{amount}",
                                  { amount: displayPricingSummary.baseTotal.toFixed(2) }
                                )}
                              </p>
                            </>
                          )}
                          {displayPricingSummary.voucher &&
                            displayPricingSummary.voucher.kind === "promo" && (
                            <>
                              <p className="discount-line">
                                {t(
                                  "bookingDay.voucher.discountLinePromo",
                                  "Promo applied: -€{amount}",
                                  {
                                    amount: displayPricingSummary.voucher.amount.toFixed(2),
                                  }
                                )}
                              </p>
                              <p className="original-total">
                                {t(
                                  "bookingDay.voucher.originalTotalPromo",
                                  "Before promo: €{amount}",
                                  { amount: displayPricingSummary.baseTotal.toFixed(2) }
                                )}
                              </p>
                            </>
                          )}

                          <div className="confirm-wrapper">
                            <p className="note">
                              {t(
                                "bookingDay.summary.note",
                                "Please verify your details before paying. You’ll receive a booking email with all details and instructions right after a successful payment."
                              )}{" "}
                              {t(
                                "bookingDay.summary.legalPrefix",
                                "By proceeding with payment, you confirm that you have read and agree to the"
                              )}{" "}
                              <Link to="/policies">
                                {t("bookingDay.summary.legalLink", "Legal Information")}
                              </Link>{" "}
                              {t(
                                "bookingDay.summary.legalSuffix",
                                "including the Terms and Conditions, cancellation policy, and safety rules, which govern your booking."
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* === RIGHT: Payment Form (Tabbed) === */}
                      <div className="payment-pane">
                        {paySuccess ? (
                          <div className="pay-success" role="status" aria-live="polite">
                            <div className="checkmark">
                              <div className="checkmark__circle"></div>
                              <div className="checkmark__stem"></div>
                              <div className="checkmark__kick"></div>
                            </div>
                            <h3>
                              {isEventCheckout
                                ? t("bookingDay.eventCheckout.successTitle", "Event payment successful!")
                                : t("bookingDay.payment.successTitle", "Payment successful!")}
                            </h3>
                            <p>
                              {isEventCheckout
                                ? t(
                                  "bookingDay.eventCheckout.successMessage",
                                  "Your event payment is complete. We’ll follow up with the event details."
                                )
                                : t(
                                  "bookingDay.payment.successMessage",
                                  "Your booking is confirmed. A confirmation email is on its way to your inbox."
                                )}
                            </p>
                            {latestBooking?.id && !isEventCheckout && (
                              <p className="booking-reference">
                                {t("bookingDay.payment.reference", "Booking reference:")}{" "}
                                <strong>{latestBooking.id}</strong>
                              </p>
                            )}
                          </div>
                        ) : payFailed ? (
                          <div className="pay-failure" role="alert" aria-live="assertive">
                            <div className="crossmark" aria-hidden="true">
                              <div className="crossmark__circle"></div>
                              <div className="crossmark__stem"></div>
                              <div className="crossmark__kick"></div>
                            </div>
                            <h3>
                              {t(
                                "bookingDay.payment.unsuccessful",
                                "Payment could not be completed."
                              )}
                            </h3>
                            <p>
                              {payError ??
                                t("bookingDay.payment.error", "Please try again.")}
                            </p>
                            <button
                              type="button"
                              className="pay-btn"
                              onClick={handleRetryPayment}
                            >
                              {t("bookingDay.payment.retry", "Try again")}
                            </button>
                          </div>
                        ) : (
                          <>
                            <div
                              className="contact-fields"
                              role="group"
                              aria-labelledby="contact-info-heading"
                            >
                              <h3 id="contact-info-heading">
                                {t(
                                  "bookingDay.payment.contact.title",
                                  "We just need your contact info"
                                )}
                              </h3>
                              <p className="contact-hint">
                                {t(
                                  "bookingDay.payment.contact.hint",
                                  "Enter your email and phone so we can send the booking confirmation."
                                )}
                              </p>
                              <label className="contact-field">
                                <span className="contact-icon">
                                  <LuMail />
                                </span>
                                <input
                                  type="email"
                                  inputMode="email"
                                  maxLength={128}
                                  value={contactEmail}
                                  onChange={(e) => handleContactEmailChange(e.target.value)}
                                  placeholder={t(
                                    "bookingDay.payment.contact.emailPlaceholder",
                                    "Email address"
                                  )}
                                  required
                                />
                              </label>
                              <label className="contact-field contact-field--phone">
                                <span className="contact-icon">
                                  <LuPhone />
                                </span>
                                <PhoneInput
                                  id="booking-day-contact-phone"
                                  name="contactPhone"
                                  value={contactPhone}
                                  onChange={handleContactPhoneChange}
                                  required
                                  autoComplete="tel"
                                  placeholder={t(
                                    "bookingDay.payment.contact.phonePlaceholder",
                                    "Phone number"
                                  )}
                                  className="contact-phone"
                                  selectClassName="contact-phone__select"
                                  inputClassName="contact-phone__input"
                                  countryLabel={t(
                                    "bookingDay.payment.contact.countryCode",
                                    "Country code"
                                  )}
                                  numberLabel={t(
                                    "bookingDay.payment.contact.phonePlaceholder",
                                    "Phone number"
                                  )}
                                />
                              </label>
                            </div>
                            {contactError && (
                              <p className="contact-error" role="alert">
                                {contactError}
                              </p>
                            )}
                            {payError && (
                              <p className="contact-error" role="alert">
                                {payError}
                              </p>
                            )}
                            {isPaying && payStuck && (
                              <div className="pay-stuck">
                                <p className="contact-error" role="status">
                                  {t(
                                    "bookingDay.payment.stuck",
                                    "Payment is taking longer than expected."
                                  )}
                                </p>
                                <button
                                  type="button"
                                  className="pay-btn"
                                  onClick={handleResetPayment}
                                >
                                  {t("bookingDay.payment.reset", "Reset payment")}
                                </button>
                              </div>
                            )}
                            {!isEventCheckout && (
                              <div
                                className="voucher-panel"
                                role="group"
                                aria-label={t("bookingDay.voucher.label", "Voucher / promo code")}
                              >
                                <div className="voucher-header">
                                  <span>{t("bookingDay.voucher.title", "Voucher / promo code")}</span>
                                  {availableVoucher ? (
                                    <span className="voucher-badge voucher-badge--ready">
                                      {t("bookingDay.voucher.ready", "Unlocked")}
                                    </span>
                                  ) : ("")}
                                </div>
                                <div className="voucher-row">
                                  <input
                                    type="text"
                                    value={voucherInput}
                                    onChange={(e) => {
                                      setVoucherInput(e.target.value.toUpperCase());
                                      setVoucherError("");
                                      setVoucherMessage("");
                                    }}
                                    placeholder={t(
                                      "bookingDay.voucher.placeholder",
                                      "Enter voucher code"
                                    )}
                                    disabled={Boolean(appliedVoucher) || voucherValidating}
                                  />
                                  <div className="voucher-actions">
                                    {!appliedVoucher && (
                                      <button
                                        type="button"
                                        className="voucher-apply"
                                        onClick={handleApplyVoucher}
                                        disabled={voucherValidating}
                                      >
                                        {t("bookingDay.voucher.apply", "Apply")}
                                      </button>
                                    )}
                                    {appliedVoucher && (
                                      <button
                                        type="button"
                                        className="voucher-clear"
                                        onClick={handleRemoveVoucher}
                                      >
                                        {t("bookingDay.voucher.remove", "Remove")}
                                      </button>
                                    )}
                                  </div>
                                  {voucherMessage && (
                                    <p className="voucher-success voucher-message">
                                      {voucherMessage}
                                    </p>
                                  )}
                                  {voucherError && (
                                    <p className="voucher-error voucher-message" role="alert">
                                      {voucherError}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="pay-content">
                              {shouldUseStripe && !stripePromise && (
                                <p className="contact-error" role="alert">
                                  {t(
                                    "bookingDay.payment.configError",
                                    "Stripe payments are not configured yet."
                                  )}
                                </p>
                              )}
                              {shouldUseStripe && stripePromise && (
                                <>
                                  {paymentIntentLoading && (
                                    <div className="pay-loader">
                                      <div className="spinner" />
                                      <span>{t("bookingDay.payment.processing", "Processing…")}</span>
                                    </div>
                                  )}
                                  {paymentIntentError && (
                                    <p className="contact-error" role="alert">
                                      {paymentIntentError}
                                    </p>
                                  )}
                                  {paymentIntent?.clientSecret &&
                                    !paymentIntentLoading &&
                                    !paymentIntentError && (
                                      <Elements
                                        stripe={stripePromise}
                                        options={{
                                          clientSecret: paymentIntent.clientSecret,
                                          locale: stripeLocale,
                                          appearance: isCompactPayment
                                            ? stripeAppearanceCompact
                                            : stripeAppearance,
                                        }}
                                        key={`${paymentIntent.clientSecret}-${stripeLocale}`}
                                      >
                                        <StripePaymentForm
                                          onSubmit={handleStripeBookingSubmit}
                                          isPayDisabled={isPayDisabled}
                                          isPaying={isPaying}
                                          t={t}
                                          isCompactPayment={isCompactPayment}
                                        />
                                      </Elements>
                                    )}
                                </>
                              )}
                              {!shouldUseStripe && (
                                <button
                                  type="button"
                                  className="pay-btn"
                                  disabled={isPayDisabled}
                                  onClick={handleFreeBookingSubmit}
                                >
                                  {isPaying
                                    ? t("bookingDay.payment.processing", "Processing…")
                                    : t("bookingDay.payment.confirm", "Confirm booking")}
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </article>
                </section>
              )}

            </div>

            {step > 0 && !hideNavActions && (
              <button
                type="button"
                className="carousel-arrow left"
                onClick={() => goToStep(step - 1)}
                aria-label={t("bookingDay.controls.previous", "Previous")}
              >
                <span className="carousel-arrow__icon" aria-hidden="true">
                  <FaChevronLeft />
                </span>
                <span className="carousel-arrow__label">
                  {t("bookingDay.controls.previous", "Previous")}
                </span>
              </button>
            )}
            {step < 3 && (
              <button
                type="button"
                className="carousel-arrow right"
                onClick={() => goToStep(step + 1)}
                aria-label={t("bookingDay.controls.next", "Next")}
              >
                <span className="carousel-arrow__icon" aria-hidden="true">
                  <FaChevronRight />
                </span>
                <span className="carousel-arrow__label">
                  {t("bookingDay.controls.next", "Next")}
                </span>
              </button>
            )}
          </div>

          {!hideNavActions && (
            <div className="carousel-controls">
              <button
                type="button"
                className="carousel-control prev"
                onClick={() => step > 0 && goToStep(step - 1)}
                disabled={step === 0}
              >
                {t("bookingDay.controls.previous", "Previous")}
              </button>
              <button
                type="button"
                className="carousel-control next"
                onClick={() => step < 3 && goToStep(step + 1)}
                disabled={step === 3}
              >
                {step === 3
                  ? t("bookingDay.controls.finish", "Finish")
                  : t("bookingDay.controls.next", "Next")}
              </button>
            </div>
          )}

          <div
            className={`validation-message ${validationMsg ? (fadeOut ? "fade-out" : "visible") : "hidden"
              }`}
          >
            <p>{validationMsg}</p>
          </div>

        </div>

        {(step === 1 || step === 2) && (
          <div className="summary">
            <h2>
              {t(
                "bookingDay.summary.priceBanner",
                `Your rental price: €${payableTotal.toFixed(2)}`,
                { price: payableTotal.toFixed(2) }
              )}
            </h2>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BookingDay;
